<template>
  <div class="doc-page-main">
    <div class="left-container" :inert="approvalSetupMissing ? '' : null" :class="{ 'left-tab-close': closeLeftTab }">
      <div class="left-container-header">
        <span v-if="!closeLeftTab && availableDocTypes.length" class="left-container-title">Document Type</span>
        <button
          type="button"
          class="collapse-filters-button"
          :title="closeLeftTab ? 'Show Filters' : 'Hide Filters'"
          :aria-label="closeLeftTab ? 'Show Filters' : 'Hide Filters'"
          :aria-expanded="closeLeftTab ? 'false' : 'true'"
          @click="closeLeftTab = !closeLeftTab"
        >
          <img
            :style="{ transform: closeLeftTab ? 'scaleX(-1)' : 'scaleX(1)' }"
            src="https://dsmlib.logistics.systems/Icons/double-left.png"
            alt=""
            aria-hidden="true"
          />
        </button>
        <button
          v-if="closeLeftTab"
          type="button"
          class="collapsed-refresh-button"
          title="Refresh"
          aria-label="Refresh approvals"
          :disabled="pageActionsDisabled || refreshingCount > 0"
          :aria-busy="refreshingCount > 0"
          @click="refreshClicked"
        >
          <img
            src="https://dsmlib.logistics.systems/Icons/icons8-refresh-64.png"
            width="22"
            height="22"
            alt=""
            aria-hidden="true"
          />
        </button>
      </div>
      <div class="left-container-content" v-show="!closeLeftTab">
        <div v-if="availableDocTypes.length" class="doc-type-tabs" role="group" aria-label="Document type">
          <button
            type="button"
            v-for="t in availableDocTypes"
            :disabled="pageActionsDisabled"
            :key="t['Code']"
            :class="{ active: selectedDocType && selectedDocType['Code'] === t['Code'] }"
            :aria-pressed="selectedDocType && selectedDocType['Code'] === t['Code'] ? 'true' : 'false'"
            @click="selectDocType(t)"
          >
            <span>{{ t['Type'] }}</span>
            <span
              v-if="docTypeCounts[t['Code']] !== undefined"
              class="doc-type-count"
              :title="`${docTypeCounts[t['Code']]} pending document(s) loaded`"
              >{{ docTypeCounts[t['Code']] }}</span
            >
          </button>
        </div>

        <!-- Filters for every type; under "All" the payment/cheque-only filters are boxed separately below. -->
        <div class="section-title">Filters</div>
        <div class="filter-fields">
          <Dropdown
            :isDisabled="pageActionsDisabled"
            class="left-container-content-det"
            placeholder="Status"
            :items="statusType"
            itemKey="Status"
            :selectedObject.sync="selectedStatus"
          />
          <Dropdown
            :isDisabled="pageActionsDisabled"
            class="left-container-content-det"
            placeholder="Requested By"
            :items="requestedByOptions"
            itemKey="Requester"
            :selectedObject="createdby ? { Requester: createdby } : null"
            @update:selectedObject="createdby = $event ? $event.Requester : ''"
          />
          <div class="left-container-content-det amount-container">
            <div class="amount-operators" role="group" aria-label="Amount comparison">
              <button
                type="button"
                v-for="(op, i) in amountOperations"
                :disabled="pageActionsDisabled"
                :key="op.symbol"
                :class="{ active: amountOperationIndex === i }"
                :title="op.label"
                :aria-pressed="amountOperationIndex === i ? 'true' : 'false'"
                @click="amountOperationIndex = i"
                >{{ op.symbol }}</button
              >
            </div>
            <div class="amount-input-wrap">
              <Input :isDisabled="pageActionsDisabled" :placeholder="companyCurrency ? 'Amount (' + companyCurrency + ')' : 'Amount'" :inputValue.sync="amount" />
              <button
                type="button"
                class="remember-amount-pin"
                :class="{ active: amountPreferenceEnabled }"
                :disabled="pageActionsDisabled || !amountPreferenceLoaded"
                :aria-pressed="amountPreferenceEnabled ? 'true' : 'false'"
                :aria-label="amountPreferenceEnabled ? 'Stop remembering amount filter' : 'Remember amount filter'"
                :title="amountPreferenceEnabled ? 'Remember amount filter enabled (saved automatically). Click to disable.' : 'Remember this amount filter (saved automatically)'"
                @click="amountPreferenceEnabled = !amountPreferenceEnabled"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" :fill="amountPreferenceEnabled ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M12 17v5" fill="none" />
                  <path d="M9 3h6l-1 6 4 4v2H6v-2l4-4z" />
                </svg>
              </button>
            </div>
          </div>
          <Dropdown
            :isDisabled="pageActionsDisabled"
            class="left-container-content-det"
            placeholder="Show Top"
            :items="topList"
            itemKey="top"
            :selectedObject.sync="selectedTop"
          />
          <template v-if="!isAllDocTypes && !isWorkOrder">
            <MultiSelect
              :tableBoxHideColumns="['label']"
              :isDisabled="pageActionsDisabled"
              class="left-container-content-det"
              placeholder="Issued To"
              :items="issuedTo"
              itemKey="Issued To"
              saveKey="Issued To Code"
              :value.sync="selectedIssuedTo"
            />
            <Dropdown
              :isDisabled="pageActionsDisabled"
              class="left-container-content-det"
              placeholder="Payment Type"
              :items="paymentType"
              itemKey="Payment Type"
              :selectedObject.sync="selectedPaymentType"
            />
            <MultiSelect
              :tableBoxHideColumns="['label']"
              :isDisabled="pageActionsDisabled"
              class="left-container-content-det charges-filter"
              :items="chargesCode"
              :value.sync="selectedChargesCode"
              itemKey="Charge Code"
              saveKey="Charge Code"
              itemDesc="Description"
              placeholder="Charges Code"
              :isCompact="false"
              @click.native="showInfoPopup = false"
            />
          </template>
        </div>

        <BoxContainer v-if="isAllDocTypes" title="Payment & Cheque" :isCollapsible="true">
          <div class="payment-filters-fields">
            <MultiSelect
              :tableBoxHideColumns="['label']"
              :isDisabled="pageActionsDisabled"
              class="left-container-content-det"
              placeholder="Issued To"
              :items="issuedTo"
              itemKey="Issued To"
              saveKey="Issued To Code"
              :value.sync="selectedIssuedTo"
            />
            <Dropdown
              :isDisabled="pageActionsDisabled"
              class="left-container-content-det"
              placeholder="Payment Type"
              :items="paymentType"
              itemKey="Payment Type"
              :selectedObject.sync="selectedPaymentType"
            />
            <MultiSelect
              :tableBoxHideColumns="['label']"
              :isDisabled="pageActionsDisabled"
              class="left-container-content-det charges-filter"
              :items="chargesCode"
              :value.sync="selectedChargesCode"
              itemKey="Charge Code"
              saveKey="Charge Code"
              itemDesc="Description"
              placeholder="Charges Code"
              :isCompact="false"
              @click.native="showInfoPopup = false"
            />
          </div>
        </BoxContainer>

        <BoxContainer
          v-if="workOrderApprovalEnabled && (isAllDocTypes || isWorkOrder)"
          title="Work Order"
          :isCollapsible="true"
        >
          <div class="payment-filters-fields">
            <MultiSelect
              :tableBoxHideColumns="['label']"
              :isDisabled="pageActionsDisabled"
              class="left-container-content-det"
              placeholder="Vehicle"
              :items="vehicleList"
              itemKey="label"
              saveKey="Inventory #"
              itemDesc="Inventory Code"
              :value.sync="workOrderFilters.vehicle"
            />
            <MultiSelect
              :tableBoxHideColumns="['label']"
              :isDisabled="pageActionsDisabled"
              class="left-container-content-det"
              placeholder="Vehicle Category"
              :items="workOrderCategoryOptions"
              :value.sync="workOrderFilters.categories"
              itemKey="label"
              saveKey="Vehicle Category"
              itemDesc="Description"
            />
            <MultiSelect
              :tableBoxHideColumns="['label']"
              :isDisabled="pageActionsDisabled"
              class="left-container-content-det"
              placeholder="Team"
              :items="workOrderTeamOptions"
              :value.sync="workOrderFilters.teams"
              itemKey="Team"
              saveKey="Team"
              itemDesc="Team"
            />
          </div>
        </BoxContainer>

        <div class="left-container-content-footer">
          <div class="left-container-content-button">
            <button type="button" class="reset-filter-button" :disabled="pageActionsDisabled" @click="clearAllFilters"
              >Reset</button
            >
            <button
              type="button"
              class="refresh-button"
              :disabled="pageActionsDisabled || refreshingCount > 0"
              :aria-busy="refreshingCount > 0"
              @click="refreshClicked"
            >
              {{ refreshingCount > 0 ? 'Refreshing...' : 'Refresh' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="main-column" :aria-busy="refreshingCount > 0">
      <div class="action-bar" :inert="approvalSetupMissing ? '' : null">
        <div class="selected-count"
          >Selected <b>{{ checkedCount }}</b
          ><span v-if="selectedAmountSummary">
            &middot; <b>{{ selectedAmountSummary }}</b></span
          ></div
        >
        <button class="quick-approve-button" :disabled="pageActionsDisabled || refreshingCount > 0" @click="quickApprovedClicked"
          >Quick Approve</button
        >
        <button class="approve-button" :disabled="pageActionsDisabled || refreshingCount > 0" @click="approveButtonClick">Approve</button>
        <button class="reject-button" :disabled="pageActionsDisabled || refreshingCount > 0" @click="rejectButtonClick">Reject</button>
        <button type="button" class="api-messages-button" :disabled="approvalSetupMissing" title="Action messages" :aria-label="`Action messages: ${apiMessageCounts.success} successful, ${apiMessageCounts.error} failed. Open history`" @click="showApiMessages = true"><img src="https://dsmlib.logistics.systems/Icons/info-480x480.png" alt="" /><span v-if="apiMessageCounts.success" class="api-message-success" title="Successful actions">{{ apiMessageCounts.success }}</span><span v-if="apiMessageCounts.error" class="api-message-error" title="Failed actions">{{ apiMessageCounts.error }}</span></button>
        <span v-if="!isWorkOrder" class="action-separator" role="separator" aria-orientation="vertical"></span>
        <button
          v-if="!isWorkOrder"
          type="button"
          class="generate-button"
          :disabled="pageActionsDisabled || refreshingCount > 0"
          @click="generateButtonClick"
          >Generate List</button
        >
        <div class="action-display-options">
          <label class="display-option">
            <input type="checkbox" v-model="showRejectedColumn" :disabled="pageActionsDisabled" /> Show Rejected
          </label>
          <div class="last-refreshed" :title="lastRefreshed ? 'Last refreshed: ' + lastRefreshed : 'Not refreshed yet'">
            <span>Last refreshed:</span>
            <time>{{ lastRefreshed ? lastRefreshed.slice(0, 16) : '—' }}</time>
          </div>
        </div>
        <div v-if="appliedFilters && activeFilterChips.length" class="filters-summary">
          <div class="filters-text" v-if="appliedFilters">
            <div
              v-for="chip in activeFilterChips"
              :key="chip.key"
              class="filter-chip"
              :title="`${chip.label}${chip.key === 'amount' && companyCurrency ? ' (' + companyCurrency + ')' : ''}: ${chip.value}`"
            >
              <span class="caption">{{ chip.label }}{{ chip.key === 'amount' && companyCurrency ? ' (' + companyCurrency + ')' : '' }}:</span><span class="value">{{ chip.value }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="approvalSetupMissing" class="refresh-error" role="alert">No Document Approval setup is configured for your account in this company. Contact your administrator to configure approval access.</div>
      <div v-if="!approvalSetupMissing && refreshError && refreshingCount === 0" class="refresh-error" role="status">{{ refreshError }}</div>
      <div v-if="refreshingCount > 0" class="list-refresh-indicator" role="status" aria-live="polite">
        <img
          src="https://dsmlib.logistics.systems/Icons/Loading.gif"
          width="48"
          height="48"
          alt=""
          aria-hidden="true"
        />
        <span>Refreshing approvals...</span>
      </div>
      <div v-show="refreshingCount === 0" class="bottom-container" :inert="approvalSetupMissing ? '' : null">
        <div
          v-for="b in displayColumns"
          :key="b.Key"
          class="bucket"
          :data-source-index="b.SourceIndex"
        >
          <div class="header" :style="{ backgroundColor: b['Colour'] }">
            <!-- <button @click="selectVisibleDocOnly"></button> -->
            <Tooltip
              v-if="b.Kind === 'pending'"
              class="tooltip-button"
              icon="https://dsmlib.logistics.systems/Icons/icons8-approve.png"
              :tooltipText="bucketsSelectVisibleTooltip[b.SourceIndex]['text']"
              position="top"
              @click.native="selectVisibleDocOnly(b.SourceIndex)"
            />
            <div class="title">
              <label v-if="b.Kind === 'pending'"
                ><input
                  type="checkbox"
                  :checked="b.bucketChecked"
                  @change="setBucketChecked(b.SourceIndex, $event.target.checked)"
                />{{ b.Title }}</label
              >
              <div v-else style="text-align: center">{{ b.Title }}</div>
            </div>
            <!-- <div class="title">{{ b.Title }}</div> -->
            <div class="count"
              >Showing {{ b.FilteredCount }} of {{ b.total == null ? b.Documents.length : b.total }}</div
            >
          </div>
          <div class="content" @scroll="b.Kind === 'pending' && bucketScrolling(b.SourceIndex)">
            <div
              class="content-item"
              :class="{ checked: d.checked }"
              v-for="d in b['Documents']"
              :key="documentKey(d)"
              :style="{ display: d['show'] ? 'block' : 'none' }"
              :docIndex="sourceDocumentIndex(d)"
              :bucketIndex="documentSourceIndex(d)"
            >
              <div class="content-row">
                <CheckBox
                  v-if="b.Kind === 'pending'"
                  class="doc-no"
                  :title="d['Document No'] ? d['Document No'].trim() : ''"
                  :isChecked.sync="d['checked']"
                  :isDisabled="!canSelectDocument(d)"
                  @checkBoxClicked="documentChecked(b.SourceIndex)"
                />
                <div v-else class="doc-no">{{ d['Document No'] ? d['Document No'].trim() : '' }}</div>
                <div class="doc-tag" :class="docTagClass(d)">{{ docTypeLabel(d) }}</div>
              </div>
              <div v-if="b.Kind === 'pending' && isWorkOrderDoc(d) && String(d['Approval Status'] || '').trim() === 'Pending' && (['true', '1'].includes(String(d.Resubmitted).trim().toLowerCase()) || Number(d.Round) > 1)" class="resubmitted-flag" title="Resubmitted for approval">
                <img src="https://dsmlib.logistics.systems/Icons/pending.png" alt="" />
                <span>Resubmitted for approval</span>
              </div>
              <div class="content-row card-date-amount">
                <div>Date: {{ d['Date Issued'] ? new Date(d['Date Issued']).formatString('yyyy-MM-dd') : '' }}</div>
                <div class="amount">{{ documentCurrency(d) }} {{ $currencyFormat(d['Total Amount'] || 0) }}</div>
              </div>
              <div v-if="d['Submitted Date']" class="card-approval-date">Submitted: {{ formatDocumentDate(d['Submitted Date'], true) }}</div>
              <div v-if="d['Bucket Date'] && (!d['Submitted Date'] || new Date(d['Submitted Date']).getTime() !== new Date(d['Bucket Date']).getTime())" class="card-approval-date" title="This date determines the approval bucket.">Last updated: {{ formatDocumentDate(d['Bucket Date'], true) }}</div>
              <div class="long-text">
                <div v-if="isWorkOrderDoc(d)" :title="[d['Vehicle #'], d['Vehicle Code']].filter(Boolean).join(' - ')"
                  ><span class="long-text-label">Vehicle:</span><span class="long-text-value">{{ [d['Vehicle #'], d['Vehicle Code']].filter(Boolean).join(' - ') || '-' }}</span></div
                >
                <div v-else :title="d['Issued To'] ? d['Issued To'].trim() : ''"
                  ><span class="long-text-label">Issued To:</span><span class="long-text-value">{{ d['Issued To'] ? d['Issued To'].trim() : '' }}</span></div
                >
                <div v-if="isWorkOrderDoc(d)" :title="d['Job Description']"
                  ><span class="long-text-label">Remarks:</span><span class="long-text-value">{{ d['Job Description'] || '-' }}</span></div
                >
                <div v-else :title="d['shipper/consignee'] ? d['shipper/consignee'].trim() : ''"
                  ><span class="long-text-label">Shpr/Csgn:</span><span class="long-text-value">{{ d['shipper/consignee'] ? d['shipper/consignee'].trim() : '' }}</span></div
                >
              </div>
              <div class="content-row">
                <div
                  >Requested by:
                  {{ isWorkOrderDoc(d) ? d['Requester'] || d['User ID'] || '' : d['User ID'] || '' }}</div
                >
                <div class="icon-button">
                  <button v-if="isWorkOrderDoc(d)" type="button" class="review-link" @click="openWorkOrderReview(d)"
                    >Review</button
                  >
                  <img
                    style="cursor: auto"
                    v-if="isRejectedDocument(d)"
                    :src="
                      isWorkOrderDoc(d) ?
                        'https://dsmlib.logistics.systems/Icons/pending.png'
                      : 'https://dsmlib.logistics.systems/Icons/RejectedIconDA.png'
                    "
                    :title="d.APRemark ? `Rejected: ${d.APRemark}` : 'Rejected'"
                    alt=""
                  />
                  <img
                    style="cursor: auto"
                    v-if="d['AssignedUser'] != 'ALL'"
                    src="https://dsmlib.logistics.systems/Icons/AssignedRequired.png"
                    alt=""
                    :title="`Required ${d['AssignedUser']} Approval`"
                  />
                  <img
                    v-if="b.Kind === 'pending' && !isWorkOrderDoc(d)"
                    src="https://dsmlib.logistics.systems/Icons/TooltipInfo.png"
                    title="Additional Info"
                    @click="infoClicked($event, d)"
                    alt=""
                  />
                  <img
                    v-if="b.Kind === 'approved' && !isWorkOrderDoc(d)"
                    @click="cancelApproveDocument(d)"
                    src="https://dsmlib.logistics.systems/Icons/Document_Cancel.png"
                    title="Cancel Document"
                    alt=""
                  />
                  <img
                    v-if="d['Attachment'] > 0 && !isWorkOrderDoc(d)"
                    src="https://dsmlib.logistics.systems/Icons/Attachment.png"
                    title="View Attachment"
                    @click="showAttachment($event, d)"
                    alt=""
                  />
                </div>
              </div>
              <div v-if="b.Kind === 'rejected'" class="rejection-note">
                <b
                  >Rejected<span v-if="d['Rejected By']"> by {{ d['Rejected By'] }}</span></b
                >
                <div v-if="d['Rejected Date']">{{ reviewValue(d['Rejected Date'], 'Date') }}</div>
                <div>{{ d.APRemark || 'No rejection remark recorded.' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="info-popup" v-if="showInfoPopup" :style="infoPopupStyle">
      <div class="info-header">
        <div>{{ infoPopupDetails['Document No'] }}</div>
        <!-- <div @click="showInfoPopupDetailsMore = !showInfoPopupDetailsMore">{{showInfoPopupDetailsMore ? 'Less←' : 'More→'}}</div> -->
        <div style="user-select: none" @click="infoMoreClicked($event)">{{
          showInfoPopupDetailsMore ? 'Less←' : 'More→'
        }}</div>
        <div @click="showInfoPopup = false">⊗</div>
      </div>
      <div class="info-content">
        <!-- <div class="info-content-row" v-for="(d, i) in infoPopupDetails['AdditionalDetails']" :key="i" :style="{ display: d['visible'] ? '' : 'none' }"> -->
        <table class="info-content-table">
          <tr class="info-content-header">
            <th>#</th>
            <th>Item Desc</th>
            <th>Cur</th>
            <th
              ><div class="tableTooltip"
                >Buying<span class="tableTooltipText">{{ infoPopupDetails['TotalOffsetAmt'] }}</span></div
              ></th
            >
            <th v-if="showInfoPopupDetailsMore"
              ><div class="tableTooltip"
                >Mark Up<span class="tableTooltipText">{{ infoPopupDetails['TotalAdjustAmt'] }}</span></div
              ></th
            >
            <th v-if="showInfoPopupDetailsMore"
              ><div class="tableTooltip"
                >Selling<span class="tableTooltipText">{{ infoPopupDetails['TotalNetAmt'] }}</span></div
              ></th
            >
            <th>Ref</th>
          </tr>
          <tr class="info-content-row" v-for="(d, i) in infoPopupDetails['AdditionalDetails']" :key="i">
            <td>{{ d['SeqNo'] ? d['SeqNo'].trim() : '' }}</td>
            <td>{{ d['ChargesCode'] ? `(${d['ChargesCode']}) ${d['ItemDesc']}` : d['ItemDesc'] }}</td>
            <td>{{ documentCurrency(d) }}</td>
            <td>{{ d['OffsetAmt'] ? parseFloat(d['OffsetAmt']).toFixed(2) : '-' }}</td>
            <td v-if="showInfoPopupDetailsMore">{{ d['AdjustAmt'] ? parseFloat(d['AdjustAmt']).toFixed(2) : '-' }}</td>
            <td v-if="showInfoPopupDetailsMore">{{ d['NetAmt'] ? parseFloat(d['NetAmt']).toFixed(2) : '-' }}</td>
            <td>{{ d['RefNo'] ? d['RefNo'].trim() : '' }}</td>
          </tr>
        </table>
        <!-- <div class="info-content-header">
          <div>#</div>
          <div>Item Desc</div>
          <div>Cur</div>
          <div style="text-align: right;">Amt</div>
          <div style="text-align: right;" v-if="showInfoPopupDetailsMore" >Adj</div>
          <div style="text-align: right;" v-if="showInfoPopupDetailsMore">Net</div>
          <div>Ref</div>
        </div>
        <div class="info-content-row" v-for="(d, i) in infoPopupDetails['AdditionalDetails']" :key="i" >
          <div>{{ d['SeqNo'] ? d['SeqNo'].trim() : '' }}</div>
          <div>{{ d['ItemDesc'] ? d['ItemDesc'].trim() : '' }}</div>
          <div>{{ documentCurrency(d) }}</div>
          <div style="text-align: right;">{{ d['OffsetAmt'] ? parseFloat(d['OffsetAmt']).toFixed(2) : '-' }}</div>
          <div style="text-align: right;" v-if="showInfoPopupDetailsMore">{{ d['AdjustAmt'] ? parseFloat(d['AdjustAmt']).toFixed(2) : '-' }}</div>
          <div style="text-align: right;" v-if="showInfoPopupDetailsMore">{{ d['NetAmt'] ? parseFloat(d['NetAmt']).toFixed(2) : '-' }}</div>
          <div>{{ d['RefNo'] ? d['RefNo'].trim() : ''}}</div>
        </div> -->
      </div>
    </div>

    <div
      class="attachments-projector-container"
      :class="{ 'workorder-attachment-preview': returnToWorkOrderReview || showApprovePopup }"
      v-if="showAttachmentPopup"
      role="dialog"
      aria-label="Attachment viewer"
      aria-modal="true"
    >
      <button type="button" class="close-button" aria-label="Close attachments" @click="closeshowAttachmentPopup()"
        >&times;</button
      >
      <div class="attachments-projector" v-if="attachmentList.length > 0">
        <div class="attachment-projection-container">
          <div v-if="attachmentList.length > 1" class="previous-attachment">
            <button type="button" aria-label="Previous attachment" @click="navAtt('prev')">&lt;</button>
          </div>
          <div class="attachment-projection">
            <img v-if="attachmentPreviewKind === 'image'" :src="attToView.AttImage" alt="Attachment preview" />
            <object v-else-if="attachmentPreviewKind === 'pdf'" type="application/pdf" :data="attToView.AttImage">
              <button type="button" @click="downloadpop()">Download file</button>
            </object>
            <audio v-else-if="attachmentPreviewKind === 'audio'" controls :src="attToView.AttImage" />
            <div v-else class="attachment-preview-unavailable">
              <span>This file cannot be previewed. Download it to view.</span>
              <button type="button" @click="downloadpop()">Download file</button>
            </div>
          </div>
          <div v-if="attachmentList.length > 1" class="next-attachment">
            <button type="button" aria-label="Next attachment" @click="navAtt('next')">&gt;</button>
          </div>
        </div>
        <div class="attachment-details">
          <div class="count"
            ><b>{{ attachmentList.indexOf(attToView) + 1 }}</b> / {{ attachmentList.length }} Attachments</div
          >
          <div class="attchWin-info">
            <table v-if="returnToWorkOrderReview">
              <tr
                ><th>Work Order</th><td>{{ attToView['Job No'] }}</td></tr
              >
              <tr
                ><th>Reference No</th><td>{{ attToView.Status }}</td></tr
              >
              <tr
                ><th>Type</th><td>{{ attToView.AttImageType }}</td></tr
              >
              <tr
                ><th>Document Date</th><td>{{ attToView['Document Date'] }}</td></tr
              >
              <tr
                ><th>Remarks</th><td>{{ attToView.Remarks || '-' }}</td></tr
              >
            </table>
            <button type="button" class="attachment-download" @click="downloadpop()">Download file</button>
            <table id="infoHeader" v-if="!returnToWorkOrderReview">
              <tr>
                <th> Payment No </th>
                <td>:{{ attToView['Job No'] }}</td>
              </tr>
              <tr>
                <th> Remarks </th>
                <td>:{{ attToView['Remarks'] ? attToView['Remarks'] : '-' }} </td>
              </tr>
            </table>
            <table id="tableContent" v-if="!returnToWorkOrderReview">
              <tr>
                <th> # </th>
                <td>{{ attToView['#'] ? attToView['#'] : '-' }} </td>
              </tr>
              <tr>
                <th> Reference No </th>
                <td>{{ attToView['Reference No'] ? attToView['Reference No'] : '-' }} </td>
              </tr>
              <tr>
                <th> Category </th>
                <td>{{ attToView['Category'] ? attToView['Category'] : '-' }} </td>
              </tr>
              <tr>
                <th> Type </th>
                <td>{{ attToView['Type'] ? attToView['Type'] : '-' }} </td>
              </tr>
              <tr>
                <th> Received Time </th>
                <td>{{ attToView['Received DateTime'] ? attToView['Received DateTime'] : '-' }} </td>
              </tr>
              <tr>
                <th> Document Date </th>
                <td>{{ attToView['Document Date'] ? attToView['Document Date'] : '-' }} </td>
              </tr>
              <tr>
                <th> Uploaded By </th>
                <td>{{ attToView['UserID'] ? attToView['UserID'] : '-' }} </td>
              </tr>
              <tr>
                <th> Job No </th>
                <td>{{ attToView['JobNo'] ? attToView['JobNo'] : '-' }} </td>
              </tr>
              <tr>
                <th> Shipper / Consignee</th>
                <td>{{ attToView['shipper/consignee'] ? attToView['shipper/consignee'] : '-' }} </td>
              </tr>
              <tr>
                <th> Payee/Issued To </th>
                <td>{{ attToView['Issued To'] ? attToView['Issued To'] : '-' }} </td>
              </tr>
              <tr>
                <th> BL Number </th>
                <td>{{ attToView['BL No'] ? attToView['BL No'].trim() : '-' }} </td>
              </tr>
              <tr>
                <th> Container No </th>
                <td
                  ><div v-for="c in containers" :key="c" style="padding: 0">{{ c }}</div>
                </td>
              </tr>
              <tr>
                <th> Total Amount </th>
                <td
                  >{{ (attToView['Currency'] || '').trim() }}
                  {{ $currencyFormat(attToView['Total Amount'] || 0) }}
                </td>
              </tr>
            </table>
            <div v-if="!returnToWorkOrderReview"> Charges Code for {{ attToView['Job No'] }} </div>
            <div class="attachmentAddInfoMain" v-if="!returnToWorkOrderReview">
              <div class="addInfoRow" v-for="(a, i) in attAdditionalDetails" :key="'addinfo' + i">
                <div style="width: 20px; min-width: 20px">{{ a['SeqNo'] }}</div>
                <div style="width: 120px; min-width: 120px">{{ a['ItemDesc'].trim() }}</div>
                <!-- <div style="width: 100px; min-width: 100px; display: flex; align-items: center; justify-content: space-between;"> -->
                <div
                  style="
                    width: 100px;
                    min-width: 100px;
                    display: flex;
                    align-items: center;
                    justify-content: space-evenly;
                  "
                >
                  <span>{{ a['Currency'].trim() }}</span>
                  <span style="">{{ parseFloat(a['FOffsetAmt']).toFixed(2) }}</span>
                </div>
                <div style="flex: 1">{{ a['Remark'] ? a['Remark'] : '' }}</div>
                <div style="width: 70px; min-width: 70px"> {{ a['RefNo'] ? a['RefNo'] : '-' }} </div>
              </div>
            </div>
            <!-- <button @click="appPopup(attachmentSel, attachmentAction);">{{attachmentAction == 'approve' ? ('Approve ' + attachmentSel['Document Type']) : ('Unapprove ' + attachmentSel['Document Type'])}}</button> -->
          </div>
        </div>
      </div>
      <div v-else class="attachments-projector attachment-empty">
        <div style="margin: 0 auto">No Attachments Found</div>
      </div>
    </div>

    <PopupV2 :show="showApiMessages" title="API Message History" icon="-" width="min(850px, 90vw)" :buttons="[{ text: 'Close' }]" @buttonClicked="showApiMessages = false">
      <div class="api-message-history">
        <p v-if="!apiMessages.length">No API messages recorded yet.</p>
        <article v-for="entry in apiMessages" :key="entry.id" :class="['api-message-entry', entry.status === 'Success' ? 'api-message-success' : 'api-message-error']">
          <div class="api-message-summary">[{{ entry.date }}] {{ entry.documentNo }} {{ entry.outcome }}</div>
          <div v-if="entry.message" class="api-message-text">{{ entry.message }}</div>
        </article>
      </div>
    </PopupV2>
    <PopupV2
      :show="showApprovePopup"
      :title="`${popupEntryRequest} Documents`"
      icon="-"
      class="decision-popup"
      :buttons="null"
      width="min(1000px, 90vw)"
    >
      <div class="decision-content">
        <div class="decision-summary">
          <span>Selected {{ selectedCheckedDocument.length }} document(s) &middot; Total <b>{{ selectedAmountSummary }}</b></span>
          <label v-if="selectedCheckedDocument.length > 1"><input type="checkbox" v-model="useSharedRemark" :disabled="loading" /> Use the same remark for all</label>
        </div>
        <RemarksBox
          v-if="useSharedRemark"
          title="Shared decision remark"
          :maxLength="200"
          :height="40"
          :isDisabled="loading"
          :isRequired="popupEntryRequest === 'Reject'"
          :text.sync="sharedDecisionRemark"
          class="shared-decision-remark"
          :class="{ 'invalid-remark': decisionRequiredErrors.length && !sharedDecisionRemark.trim() }"
          ref="sharedDecisionRemarkInput"
        />
        <div
          class="decision-document-wrapper"
          v-for="(i, index) in selectedCheckedDocument"
          :key="documentKey(i)"
          :ref="'decision-document-' + index"
        >
          <BoxContainer class="decision-document">
            <div class="decision-card-title">
              <span :title="i['Document No'] || String(i.TxnID || '')">{{ index + 1 }}) <b>{{ i['Document No'] || i.TxnID }}</b></span>
              <span class="decision-type" :class="docTagClass(i)">{{ docTypeLabel(i) }}</span>
            </div>
            <b class="decision-card-amount">{{ documentCurrency(i) }} {{ $currencyFormat(i['Total Amount'] || 0) }}</b>
            <div class="decision-card-body">
              <dl class="decision-details">
                <div v-for="field in decisionDetails(i)" :key="field.label">
                  <dt>{{ field.label }}</dt><dd :title="String(field.value)">{{ field.value }}</dd>
                </div>
              </dl>
              <dl v-if="isWorkOrderDoc(i)" class="decision-details">
                <div><dt>Status</dt><dd>{{ approvalStatusLabel(i['Approval Status']) }}<span v-if="i.Round"> (Round {{ i.Round }})</span></dd></div>
                <div><dt>Changes</dt><dd :title="changeSummary(workOrderReviews[String(i['Document No'] || '').trim()])">{{ changeSummary(workOrderReviews[String(i['Document No'] || '').trim()]) }}</dd></div>
                <div><dt>Review</dt><dd class="decision-review-state">
                  <button type="button" class="review-link" :disabled="loading" @click="openWorkOrderReview(i, false, true)">Review job and history</button>
                  <span :class="{ reviewed: reviewWasDisplayed(i) }">{{ reviewWasDisplayed(i) ? 'Reviewed' : 'Not reviewed yet' }}</span>
                </dd></div>
              </dl>
              <div v-else class="decision-attachments">
                <div class="decision-attachment-header">
                  <b>Attachments</b>
                  <button v-if="Number(i.Attachment) > 0" type="button" class="review-link" :disabled="loading" @click="showAttachment($event, i)">View existing ({{ i.Attachment }})</button>
                  <span v-else class="decision-muted">none yet</span>
                </div>
                <template v-if="popupEntryRequest === 'Approve'">
                  <div class="decision-upload-row">
                    <Input
                      :key="documentKey(i) + '-' + showApprovePopup"
                      :isDisabled="loading"
                      textType="file"
                      :multiFile="true"
                      placeholder="Attach files"
                      class="decision-upload"
                      @fileChanged="documentSelected($event, index)"
                    />
                    <span class="decision-file-names" :class="{ 'decision-muted': !i.documents || !i.documents.length }" :title="(i.documents || []).map((file) => file.docatt).join(', ')">{{ (i.documents || []).map((file) => file.docatt).join(', ') || 'No file selected' }}</span>
                  </div>
                  <Input placeholder="Attachment Remarks" :inputValue.sync="i.documentremarks" :isDisabled="loading" validator="text,{maxLength:200}" class="decision-attachment-remark" />
                </template>
                <span v-else class="decision-muted decision-attachment-note">New attachments are only added when approving.</span>
              </div>
            </div>
            <RemarksBox
              v-if="!useSharedRemark"
              :title="`${popupEntryRequest} Remarks`"
              :maxLength="200"
              :height="40"
              :isDisabled="loading"
              :text.sync="i.approveremarks"
              :isRequired="popupEntryRequest === 'Reject'"
              class="decision-document-remark"
              :class="{ 'invalid-remark': decisionRequiredErrors.some((error) => error.Key === index) && !String(i.approveremarks || '').trim() }"
            />
          </BoxContainer>
        </div>
      </div>
      <div class="decision-footer">
        <div class="decision-messages" aria-live="polite">
          <div v-if="decisionRequiredErrors.length" class="decision-required-message">
            <template v-if="useSharedRemark">Enter a shared rejection remark. <button type="button" class="review-link" @click="focusDecisionDocument(decisionRequiredErrors[0].Key)">Go to remark</button></template>
            <template v-else><span>Enter a rejection remark for:</span><div class="decision-required-documents"><button v-for="error in decisionRequiredErrors" :key="error.Key" type="button" class="review-link" @click="focusDecisionDocument(error.Key)">{{ error.Value }}</button></div></template>
          </div>
          <div v-for="message in decisionWarnings" :key="message">{{ message }}</div>
          <div v-for="message in Emessages" :key="'error-' + message">{{ message }}</div>
          <div v-for="message in successMessages" :key="'success-' + message">{{ message }}</div>
        </div>
        <div class="decision-buttons">
          <button v-for="button in decisionPopupButtons" :key="button.key" :class="{ 'approve-button': button.text === 'Approve', 'reject-button': button.text === 'Reject' }" type="button" :disabled="button.disabled" @click="approveClick"><img :src="button.icon" alt="" />{{ button.text }}</button>
          <button type="button" :disabled="loading" @click="cancelClick"><img src="https://dsmlib.logistics.systems/Icons/Cancel.png" alt="" />Cancel</button>
        </div>
      </div>
    </PopupV2>

    <PopupV2
      :show="showWorkOrderReview"
      :title="reviewTitle"
      icon="-"
      class="workorder-review-popup"
      :buttons="null"
      width="min(1000px, 90vw)"
      height="90vh"
      @buttonClicked="closeWorkOrderReview"
    >
      <div class="workorder-review" :aria-busy="workOrderReviewLoading" style="overflow: auto">
        <div v-if="workOrderReviewLoading" class="review-loading" role="status"
          ><img src="https://dsmlib.logistics.systems/Icons/Loading.gif" width="48" height="48" alt="" /><span
            >Loading saved job details...</span
          ></div
        >
        <p v-else-if="workOrderReviewError" role="alert"
          >{{ workOrderReviewError }} <button type="button" @click="reloadWorkOrderReview">Reload review</button></p
        >
        <template v-else-if="workOrderReview">
          <BoxContainer title="Job Details" class="review-job-box">
            <div class="review-job-details">
              <Input
                placeholder="Vehicle # - Vehicle Code"
                :isDisabled="true"
                :inputValue="reviewVehicle || '-'"
                class="review-vehicle"
              />
              <div class="review-job-summary">
                <div class="review-summary-box"
                  >Total Job Price:
                  <b>{{ reviewCurrency }} {{ $currencyFormat(workOrderReview.Header.TotalAmount || 0) }}</b></div
                >
                <div class="review-summary-box"
                  >Approval: {{ approvalStatusLabel(workOrderReview.State['Approval Status']) }}</div
                >
              </div>
              <Input
                v-for="field in reviewHeaderFields"
                :key="field.label"
                :placeholder="field.label"
                :inputValue="field.value"
                :isDisabled="true"
              />
            </div>
            <RemarksBox
              class="review-job-description"
              title="Remarks"
              :text="workOrderReview.Header.Remark1 || ''"
              :isDisabled="true"
              :maxLength="200"
              :height="40"
            />
            <footer v-if="workOrderApprovalEnabled && workOrderReview.State['Approval Status'] !== 'Disabled' && workOrderReview.AuditWarning" class="review-audit-warning" role="status"><b>Change tracking unavailable.</b> {{ workOrderReview.AuditWarning }}</footer>
          </BoxContainer>
          <div v-if="reviewBaseline" class="review-change-note">
            <span
              ><span class="status-chip status-chip--ok">New</span> / <span class="status-chip status-chip--warn">Edited</span> = since last
              {{ reviewBaseline.kind === 'Rejected' ? 'rejection' : 'full approval' }} ({{
                reviewValue(reviewBaseline.date, 'Date')
              }})</span
            >
            <label><input type="checkbox" v-model="showReviewChangesOnly" :disabled="!!workOrderReview.AuditWarning" /> Show changes only</label>
          </div>
          <div class="review-sections">
            <BoxContainer v-if="reviewTasksSection" :title="reviewTasksSection.heading">
              <div class="review-section-content">
                <div class="review-section-table">
                  <TableBox
                    v-if="reviewTasksSection.rows.length"
                    :key="reviewTasksSection.title + ':' + reviewTasksSection.columns.join('|')"
                    class="review-table"
                    title=""
                    :tableData="reviewTableData['Tasks'] || []"
                    :fitContentWidth="true"
                    :dataType="reviewTasksSection.dataType"
                    :allowEmptyNumeric="true"
                    :hideColumns="['_ReviewIndex', '_ServiceGroup']"
                    :hideSearch="true"
                    :hideBottomBlock="true"
                    :maxSelection="0"
                    groupBy="_ServiceGroup"
                    :rowHeight="24"
                  />
                  <p v-if="!reviewTasksSection.rows.length" class="review-empty">{{
                    reviewTasksSection.count ? 'No changed rows.' : (
                      'No ' + reviewTasksSection.title.toLowerCase() + ' recorded.'
                    )
                  }}</p>
                </div>
              </div>
            </BoxContainer>
            <BoxContainer v-if="reviewPartsSection" :title="reviewPartsSection.heading">
              <div class="review-section-content">
                <div class="review-section-table">
                  <TableBox
                    v-if="reviewPartsSection.rows.length"
                    :key="reviewPartsSection.title + ':' + reviewPartsSection.columns.join('|')"
                    class="review-table"
                    title=""
                    :tableData="reviewTableData['Parts / Labour'] || []"
                    :fitContentWidth="true"
                    :dataType="reviewPartsSection.dataType"
                    :allowEmptyNumeric="true"
                    :hideColumns="['_ReviewIndex', '_ServiceGroup']"
                    :hideSearch="true"
                    :hideBottomBlock="true"
                    :maxSelection="0"
                  />
                  <div v-if="reviewPartsSection.rows.length" class="review-parts-total">
                    <b>{{ $currencyFormat(reviewPartsSection.total) }}</b>
                    {{
                      showReviewChangesOnly && reviewBaseline ?
                        'shown rows (job total ' +
                        reviewCurrency +
                        ' ' +
                        $currencyFormat(workOrderReview.Header.TotalAmount || 0) +
                        ')'
                      : reviewCurrency + ' TOTAL'
                    }}
                  </div>
                  <p v-if="!reviewPartsSection.rows.length" class="review-empty">{{
                    reviewPartsSection.count ? 'No changed rows.' : (
                      'No ' + reviewPartsSection.title.toLowerCase() + ' recorded.'
                    )
                  }}</p>
                </div>
              </div>
            </BoxContainer>
            <BoxContainer v-if="reviewTyresSection" :title="reviewTyresSection.heading">
              <div class="review-section-content" :class="{ 'review-tyre-layout': reviewTyreLayout.length }">
                <TyreGrid
                  v-if="reviewTyreLayout.length"
                  class="review-tyre-grid"
                  :tyreLayout="reviewTyreLayout"
                  layoutType="LeftRight"
                  :showAll="false"
                  :showSpares="false"
                />
                <div class="review-section-table">
                  <TableBox
                    v-if="reviewTyresSection.rows.length"
                    :key="reviewTyresSection.title + ':' + reviewTyresSection.columns.join('|')"
                    class="review-table"
                    title=""
                    :tableData="reviewTableData['Tyre Changes'] || []"
                    :fitContentWidth="true"
                    :dataType="reviewTyresSection.dataType"
                    :allowEmptyNumeric="true"
                    :hideColumns="['_ReviewIndex', '_ServiceGroup']"
                    :hideSearch="true"
                    :hideBottomBlock="true"
                    :maxSelection="0"
                  />
                  <p v-if="!reviewTyresSection.rows.length" class="review-empty">{{
                    reviewTyresSection.count ? 'No changed rows.' : (
                      'No ' + reviewTyresSection.title.toLowerCase() + ' recorded.'
                    )
                  }}</p>
                  <p v-if="reviewTyreLayoutLoading" class="review-empty" role="status">Loading tyre positions...</p>
                </div>
              </div>
            </BoxContainer>
            <BoxContainer v-if="reviewDocumentsSection" :title="reviewDocumentsSection.heading">
              <div class="review-section-content">
                <div class="review-section-table">
                  <TableBox
                    v-if="reviewDocumentsSection.rows.length"
                    :key="reviewDocumentsSection.title + ':' + reviewDocumentsSection.columns.join('|')"
                    class="review-table review-documents-table"
                    title=""
                    :tableData="reviewTableData['Documents'] || []"
                    :fitContentWidth="true"
                    :dataType="reviewDocumentsSection.dataType"
                    :allowEmptyNumeric="true"
                    :hideColumns="['_ReviewIndex', '_ServiceGroup']"
                    :hideSearch="true"
                    :hideBottomBlock="true"
                    :maxSelection="0"
                    :additionalButtons="[
                      { text: 'View file', index: 0, width: 104, disabled: reviewDocumentUnavailable },
                    ]"
                    @buttonClicked="(text, row) => reviewTableButtonClicked(reviewDocumentsSection, text, row)"
                  />
                  <p v-if="!reviewDocumentsSection.rows.length" class="review-empty">{{
                    reviewDocumentsSection.count ? 'No changed rows.' : (
                      'No ' + reviewDocumentsSection.title.toLowerCase() + ' recorded.'
                    )
                  }}</p>
                </div>
              </div>
            </BoxContainer>
            <BoxContainer v-if="reviewHistorySection" :title="reviewHistorySection.heading" class="review-history">
              <div class="review-section-content">
                <div class="review-section-table">
                  <TableBox
                    v-if="reviewHistorySection.rows.length"
                    :key="reviewHistorySection.title + ':' + reviewHistorySection.columns.join('|')"
                    class="review-table"
                    title=""
                    :tableData="reviewTableData['Approval History'] || []"
                    :fitContentWidth="true"
                    :dataType="reviewHistorySection.dataType"
                    :allowEmptyNumeric="true"
                    :hideColumns="['_ReviewIndex', '_ServiceGroup']"
                    :hideSearch="true"
                    :hideBottomBlock="true"
                    :maxSelection="0"
                  />
                  <p v-if="!reviewHistorySection.rows.length" class="review-empty">{{
                    reviewHistorySection.count ? 'No changed rows.' : (
                      'No ' + reviewHistorySection.title.toLowerCase() + ' recorded.'
                    )
                  }}</p>
                </div>
              </div>
            </BoxContainer>
          </div>
        </template>
      </div>
      <div class="review-decision-footer">
        <div v-if="canDecideReview" class="review-decision-remark"
          ><RemarksBox
            title="Approve / Reject Remarks"
            :maxLength="200"
            :height="40"
            :isDisabled="reviewDecisionBusy"
            :errorMessage="reviewRemarkRequired ? 'Reject remark is required.' : ''"
            :text.sync="reviewDecisionRemark"
            :class="{ 'invalid-remark': reviewRemarkRequired }"
        /></div>
        <div v-if="reviewDecisionError && !reviewRemarkRequired" class="review-decision-error" role="alert"
          >{{ reviewDecisionError }}
          <button type="button" :disabled="reviewDecisionBusy" @click="reloadWorkOrderReview"
            >Reload review</button
          ></div
        >
        <div class="review-footer-actions">
          <button
            v-if="canDecideReview"
            type="button"
            :disabled="reviewDecisionBusy || refreshingCount > 0"
            class="approve-button"
            @click="decideFromReview('Approved')"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="11" fill="#2cae48" />
              <path d="m6 12 4 4 8-8" fill="none" stroke="white" stroke-width="2.5" />
            </svg>
            <span>{{ reviewDecisionBusy ? 'Sending...' : 'Approve' }}</span>
          </button>
          <button
            v-if="canDecideReview"
            type="button"
            :disabled="reviewDecisionBusy || refreshingCount > 0"
            class="reject-button"
            @click="decideFromReview('Rejected')"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="11" fill="#e43c36" />
              <path d="m7 7 10 10m0-10L7 17" fill="none" stroke="white" stroke-width="2.5" />
            </svg>
            <span>Reject</span>
          </button>
          <button type="button" :disabled="reviewDecisionBusy" @click="closeWorkOrderReview">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="11" fill="#888" />
              <path d="m7 7 10 10m0-10L7 17" fill="none" stroke="white" stroke-width="2.5" />
            </svg>
            <span>Close</span>
          </button>
        </div>
      </div>
    </PopupV2>

    <PopupV2
      :show="showGenerateListPopup"
      @buttonClicked="generateListButtonClick"
      title="Generate Approved PV Pre-authorize List"
      :buttons="buttonsArray"
      height="80%"
      width="45%"
    >
      <div class="generateList-main">
        <div class="generateList-filter">
          <!-- <Dropdown placeholder="Bank" isRequired="true" :items="bankCode" itemKey="BankCodeBankName" :selectedObject.sync="selectedBankCode" @selected="getGenerateListTableData(...arguments, 'GETALLFILENO')" /> -->
          <Dropdown
            placeholder="Bank"
            isRequired="true"
            :items="bankCode"
            itemKey="BankCodeBankName"
            :selectedObject.sync="selectedBankCode"
          />
          <Dropdown placeholder="File" :items="fileno" itemKey="FileNo" :selectedObject.sync="selectedFileNo" />
        </div>
        <div class="generateList-table">
          <TableBox
            :tableData="fileNoDisplayTableData"
            :title="generateListTableTitle"
            maxSelection="0"
            :downloadName="generateListTableTitle"
            :hideColumns="['FileNo', 'Document Link']"
            :dataType="{ [companyCurrency || 'Amount']: 'number', 'PV Date': 'date' }"
            :excelColumns="excelColumns"
          />
        </div>
      </div>
    </PopupV2>

    <Loader :isVisible.sync="loading" :loadingText="loadText" />
    <Loader :isVisible="reviewDecisionBusy" loadingText="Sending approval decision..." />

    <div class="designerv2-popup" v-if="showDesignerV2">
      <DesignerV2
        :show.sync="showDesignerV2"
        :viewMode="true"
        reportFolder="ROTForm"
        :reportFile="designerV2ReportFile"
        :reportData="designerV2Data"
      />
    </div>
  </div>
</template>

<script>
// import { threadId } from 'worker_threads';
import TableBox from '../../../components/v2testComponent/TableBox.vue';
import MultiSelect from '../../../components/v2testComponent/MultiSelect.vue';
import TyreGrid from '../../../components/TyreGrid.vue';
/* eslint-disable */
export default {
  components: {
    TableBox,
    MultiSelect,
    TyreGrid,
  },
  data: function () {
    return {
      loading: false,
      initialDataLoading: true,
      loadText: 'Loading',
      refreshingCount: 0,

      getDocApprovalPromises: [],

      userid: '',
      lastRefreshed: '',

      menuOpen: true,

      closeLeftTab: false,
      narrowWindow: false,
      closeFilterMesgTab: true,
      docTypeCounts: {},
      workOrderApprovalEnabled: false,
      workOrderApprovalAccess: null,
      legacyApprovalAccess: { Payment: null, Cheque: null },

      docType: [
        { '#': 1, Type: 'All', Code: 'ALL' },
        { '#': 2, Type: 'Payment', Code: 'Payment' },
        { '#': 3, Type: 'Cheque', Code: 'Cheque' },
        { '#': 4, Type: 'Work Order', Code: 'EMSJOB' },
      ],
      topList: [{ top: '50' }, { top: '100' }, { top: '500' }, { top: 'ALL' }],
      statusType: [
        { '#': 1, Status: 'All' },
        { '#': 2, Status: 'Pending' },
        { '#': 3, Status: 'Rejected' },
      ],
      selectedDocType: null,
      selectedTop: null,
      selectedStatus: null,
      refreshDocType: null,
      activeFilterChips: [],
      appliedFilters: null,

      issuedTo: [],
      selectedIssuedTo: [],

      paymentType: [],
      selectedPaymentType: null,

      createdby: '',
      vehicleList: [],
      vehicleCategoryList: [],
      workOrderFilters: { vehicle: [], categories: [], teams: [] },

      chargedescription: '',

      chargesCode: [],
      selectedChargesCode: [],

      amountOperationIndex: 0,
      amountOperations: [
        { symbol: '≥', code: '>=', label: 'At least' },
        { symbol: '≤', code: '<=', label: 'At most' },
        { symbol: '=', code: '=', label: 'Exactly' },
      ],
      amount: '',
      amountPreferenceEnabled: false,
      amountPreferenceLoaded: false,
      savedAmountPreference: '',
      amountPreferenceTimer: null,
      amountPreferenceSaving: false,

      showInfoPopup: false,
      infoPopupStyle: {},
      infoPopupDetails: null,
      showInfoPopupDetailsMore: false,

      checkedCount: 0,
      selectedCheckedDocument: [],

      bucketsFilteredCount: 0,

      buckets: [],
      bucketsSelectVisibleTooltip: [],

      showApprovePopup: false,
      showApiMessages: false,
      apiMessages: [],
      apiMessageId: 0,
      refreshError: '',
      refreshRequestId: 0,
      showWorkOrderReview: false,
      workOrderReview: null,
      workOrderReviewLoading: false,
      workOrderReviewError: '',
      returnToWorkOrderReview: false,
      workOrderReviews: {},
      displayedWorkOrderReviews: {},
      workOrderReviewDocument: null,
      workOrderReviewRequestId: 0,
      reviewDecisionRemark: '',
      reviewFromDecisionPopup: false,
      reviewDecisionBusy: false,
      reviewDecisionError: '',
      reviewRemarkRequired: false,
      showReviewChangesOnly: false,
      reviewTyrePositions: [],
      reviewTableData: {},
      reviewTableRenderVersion: 0,
      reviewTyreLayoutLoading: false,
      showRejectedColumn: false,
      approvalPreferenceKey: '',
      useSharedRemark: false,
      sharedDecisionRemark: '',
      decisionRequiredErrors: [],

      hidepdf: true,
      showAttachmentPopup: false,
      attachmentList: [],
      attToView: [],
      attAdditionalDetails: [],

      popupEntryRequest: '',
      Emessages: [],
      successMessages: [],

      showDesignerV2: false,
      designerV2Data: null,
      designerV2ReportFile: ['DocumentApprovalList.json'],

      //#region Generate List Popup
      showGenerateListPopup: false,
      buttonsArray: [{ text: 'Generate New List' }, { text: 'Print List' }, { text: 'Cancel' }],
      generateListTableTitle: '',

      bankCode: [],
      selectedBankCode: null,

      fileno: [],
      // file
      selectedFileNo: null,
      newFileNo: null,

      fileNoTableData: [],

      companyCurrency: '',
      //#endregion
    };
  },
  props: {},
  computed: {
    localFilters() {
      return { documentType: this.selectedDocType?.Code || 'ALL', selectedTop: this.selectedTop, selectedStatus: this.selectedStatus, selectedIssuedTo: this.selectedIssuedTo, selectedPaymentType: this.selectedPaymentType, createdby: this.createdby, selectedChargesCode: this.selectedChargesCode, amount: this.amount, amountOperationIndex: this.amountOperationIndex, workOrderFilters: this.workOrderFilters };
    },
    amountPreference() {
      return this.amountPreferenceEnabled ? { remember: true, amount: String(this.amount ?? '').trim(), operator: this.amountOperations[this.amountOperationIndex].code } : { remember: false };
    },
    fileNoDisplayTableData() {
      return this.fileNoTableData.map((row) =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => [key === 'RM' ? this.companyCurrency || 'Amount' : key, value]),
        ),
      );
    },
    excelColumns() {
      return [
        { key: 'No.', title: 'No.' },
        { key: 'Payee', title: 'Payee' },
        { key: 'PV No.', title: 'PV No.' },
        { key: 'Doc Ref.', title: 'Doc Ref.' },
        { key: this.companyCurrency || 'Amount', title: this.companyCurrency || 'Amount' },
        { key: 'ROC', title: 'ROC' },
        { key: 'Bank Name(Acc No.)', title: 'Bank Name(Acc No.)' },
        { key: 'PV Date', title: 'PV Date' },
        { key: 'Document Link', title: 'Document Link' },
      ];
    },
    workOrderCategoryOptions() {
      return [...new Set(this.vehicleList.map((vehicle) => String(vehicle['Vehicle Category'] || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map((category) => {
        const match = this.vehicleCategoryList.find((item) => String(item.CategoryCode || '').trim().toLowerCase() === category.toLowerCase());
        const description = String(match?.CategoryDesc || '').trim();
        return { 'Vehicle Category': category, Description: description, label: description && description !== category ? `${category} - ${description}` : category };
      });
    },
    workOrderTeamOptions() {
      return [...new Set(this.vehicleList.map((vehicle) => String(vehicle.Team || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map((Team) => ({ Team }));
    },
    requestedByOptions() {
      return [...new Set(this.buckets.flatMap((bucket) => (bucket.Documents || []).map((doc) => String(this.isWorkOrderDoc(doc) ? doc.Requester || '' : doc['User ID'] || '').trim())).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map((Requester) => ({ Requester }));
    },
    displayColumns() {
      const rejected = new Map();
      const rejectedCutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const columns = this.buckets.map((bucket, sourceIndex) => {
        const approved = this.isApprovedBucket(bucket);
        const sourceDocuments = (bucket.Documents || []).filter((doc) => !this.selectedDocType || this.selectedDocType.Code === 'ALL' || (this.isWorkOrderDoc(doc) ? 'EMSJOB' : doc['Document Type'] === 'Cheque' ? 'Cheque' : 'Payment') === this.selectedDocType.Code);
        const documents = sourceDocuments.filter((doc) => {
          const rejectedNow = this.isRejectedDocument(doc);
          if (!approved && rejectedNow) {
            if (new Date(doc['Rejected Date']).getTime() >= rejectedCutoff && !rejected.has(this.documentKey(doc))) rejected.set(this.documentKey(doc), doc);
            return false;
          }
          return true;
        });
        return {
          ...bucket,
          Key: bucket.Bucket,
          SourceIndex: sourceIndex,
          Kind: approved ? 'approved' : 'pending',
          Title:
            approved ? 'My approvals · Last 7 days' : (
              String(bucket.Bucket || '').replace(/^Pending Approval/i, 'Pending')
            ),
          Documents: documents,
          total: this.selectedDocType?.Code === 'ALL' ? Math.max(documents.length, Number(bucket.total ?? bucket.Documents.length) - (bucket.Documents.length - documents.length)) : documents.length,
          FilteredCount: documents.filter((doc) => doc.show).length,
        };
      });
      if (this.showRejectedColumn) {
        const older = columns.find((column) => /^Pending Approval > 50 days$/i.test(column.Bucket));
        const pending = columns.find((column) => /^Pending Approval > 10 days$/i.test(column.Bucket));
        if (older && pending) {
          pending.Documents = [...pending.Documents, ...older.Documents];
          pending.total += older.total;
          pending.FilteredCount += older.FilteredCount;
          const selectable = pending.Documents.filter((doc) => doc.show && this.canSelectDocument(doc));
          pending.bucketChecked = selectable.length > 0 && selectable.every((doc) => doc.checked);
          columns.splice(columns.indexOf(older), 1);
        }
        const documents = [...rejected.values()];
        const rejectedColumn = {
          Key: 'rejected',
          SourceIndex: -1,
          Kind: 'rejected',
          Title: 'Rejected · Last 7 days',
          Colour: '#ffb3b3',
          Documents: documents,
          FilteredCount: documents.filter((doc) => doc.show).length,
        };
        const index = columns.findIndex((column) => column.Kind === 'approved');
        columns.splice(index < 0 ? columns.length : index + 1, 0, rejectedColumn);
      }
      return columns;
    },
    selectedAmountSummary() {
      const currencies = new Set(this.selectedCheckedDocument.map((doc) => this.documentCurrency(doc)));
      if (currencies.size !== 1 || currencies.has('')) return '';
      const total = this.selectedCheckedDocument.reduce((sum, doc) => sum + this.amountNumber(doc['Total Amount']), 0);
      return [...currencies][0] + ' ' + this.$currencyFormat(total);
    },
    reviewVehicle() {
      const header = this.workOrderReview?.Header || {};
      return [header.InventoryNo, header.VehicleCode].filter(Boolean).join(' - ');
    },
    reviewTitle() {
      const number = this.workOrderReview?.Header?.JobNo;
      return 'Work Order Review' + (number ? ' : ' + number : '');
    },
    reviewCurrency() {
      return this.companyCurrency || String(this.workOrderReview?.Header?.Currency || '').trim();
    },
    reviewBaseline() {
      return this.comparisonBaseline(this.workOrderReview);
    },
    reviewTyreLayout() {
      const changes = this.workOrderReview?.Tyres || [];
      return this.reviewTyrePositions.map((position) => ({
        ...position,
        disabled: true,
        description: this.reviewTyrePositionLabel(position.index),
        state:
          changes.some((row) => String(row.Position ?? '').trim() === String(position.index).trim()) ? 'changes' : (
            'normal'
          ),
      }));
    },
    attachmentPreviewKind() {
      const type = String(this.attToView?.AttImageType || '')
        .trim()
        .toLowerCase();
      if (type === '.pdf' || type === 'application/pdf') return 'pdf';
      if (type === '.ogg' || type.startsWith('audio/')) return 'audio';
      if (type.startsWith('image/') || ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'].includes(type))
        return 'image';
      return '';
    },
    reviewHeaderFields() {
      const h = this.workOrderReview?.Header || {};
      return [
        { label: 'Job Date', value: this.reviewValue(h.JobDate, 'Date Only') },
        { label: 'Requested By', value: h.RequestBy || '-' },
        { label: 'Service Person', value: h.ServicePerson || '-' },
        { label: 'Workshop', value: h.Workshop || '-' },
        {
          label: 'Repair Type',
          value:
            [
              h.ServicType === 'R' ? 'Repair'
              : h.ServicType ? 'Preventive'
              : '',
              String(h.IntExt) === '0' ? 'Internal'
              : h.IntExt ? 'External'
              : '',
            ]
              .filter(Boolean)
              .join(' - ') || '-',
        },
        { label: 'Mileage (KM)', value: h.MeterReading == null ? '-' : Number(h.MeterReading).toLocaleString('en-US') },
        { label: 'Date/Time In', value: this.reviewDateTime(h.DateIn, h.TimeIn) },
        { label: 'Date/Time Out', value: this.reviewDateTime(h.DateOut, h.TimeOut) },
      ];
    },
    canDecideReview() {
      return (
        !this.reviewFromDecisionPopup &&
        !!this.workOrderReview?.State?.CanApprove &&
        !this.workOrderReviewLoading &&
        !!this.workOrderReviewDocument &&
        !this.isRejectedDocument(this.workOrderReviewDocument) &&
        this.workOrderReviewDocument.Assigned !== '0' &&
        !this.isApprovedBucket(this.buckets[this.documentSourceIndex(this.workOrderReviewDocument)] || {})
      );
    },
    apiMessageCounts() {
      return { success: this.apiMessages.filter((entry) => entry.status === 'Success').length, error: this.apiMessages.filter((entry) => entry.status === 'Error').length };
    },
    decisionPopupButtons() {
      return [
        {
          key: 'decision',
          text: this.popupEntryRequest,
          disabled: this.loading || !this.selectedCheckedDocument.length,
          icon:
            this.popupEntryRequest === 'Reject' ?
              'https://dsmlib.logistics.systems/Icons/Cancel.png'
            : 'https://dsmlib.logistics.systems/Icons/icons8-approve.png',
        },
      ];
    },
    decisionWarnings() {
      if (this.popupEntryRequest !== 'Approve') return [];
      const jobs = this.selectedCheckedDocument.filter((doc) => this.isWorkOrderDoc(doc));
      const missing = jobs.filter((doc) => !this.reviewWasDisplayed(doc)).length;
      return missing ? [missing + ' of ' + jobs.length + ' work orders have not been reviewed yet.'] : [];
    },
    approvalSetupMissing() {
      return this.legacyApprovalAccess.Payment === false && this.legacyApprovalAccess.Cheque === false && this.workOrderApprovalAccess === false;
    },
    pageActionsDisabled() {
      return this.initialDataLoading || this.refreshingCount > 0 || this.approvalSetupMissing;
    },

    availableDocTypes() {
      const types = this.docType.filter((type) => type.Code !== 'ALL' && (type.Code === 'EMSJOB' ? this.workOrderApprovalEnabled && this.workOrderApprovalAccess === true : this.legacyApprovalAccess[type.Code] === true));
      return types.length > 1 ? [this.docType[0], ...types] : types;
    },
    reviewTasksSection() {
      return this.workOrderReviewSections.find((section) => section.title === 'Tasks');
    },
    reviewPartsSection() {
      return this.workOrderReviewSections.find((section) => section.title === 'Parts / Labour');
    },
    reviewTyresSection() {
      return this.workOrderReviewSections.find((section) => section.title === 'Tyre Changes');
    },
    reviewDocumentsSection() {
      return this.workOrderReviewSections.find((section) => section.title === 'Documents');
    },
    reviewHistorySection() {
      return this.workOrderReviewSections.find((section) => section.title === 'Approval History');
    },
    workOrderReviewSections() {
      const review = this.workOrderReview || {};
      const baseline = this.comparisonBaseline(review);
      const tyrePartIDs = new Set(
        (review.Tyres || []).filter((row) => row.PartID != null).map((row) => String(row.PartID).trim()),
      );
      const sections = [
        {
          title: 'Tasks',
          comparable: true,
          rows: (review.Tasks || []).map((row) => ({
            ...row,
            Service: [row['Service Code'], row['Service Description']].filter(Boolean).join(' - '),
            Task: [row['Task Code'], row['Task Description']].filter(Boolean).join(' - '),
            'Date Start': row['Date Start'] ? this.reviewDateTime(row['Date Start'], row['Time Start']) : '',
            'Date End': row['Date End'] ? this.reviewDateTime(row['Date End'], row['Time End']) : '',
          })),
          columns: ['Change', 'Task Code', 'Task Description', 'Service Person', 'Date Start', 'Date End'],
          widths: [6, 50, 12, 16, 16],
        },
        {
          title: 'Parts / Labour',
          comparable: true,
          rows: (review.Parts || []).filter((row) => row.PartID == null || !tyrePartIDs.has(String(row.PartID).trim())),
          columns: ['Change', 'Type', 'Part No', 'Serial No', 'Quantity', 'Cost (RM)', 'Remark'],
          widths: [6, 8, 13, 13, 6, 11, 43],
        },
        {
          title: 'Tyre Changes',
          comparable: true,
          rows: (review.Tyres || []).map((row) => ({
            ...row,
            Position: row.Position == null || String(row.Position).trim() === '' ? '' : this.reviewTyrePositionLabel(row.Position),
          })),
          columns: [
            'Change',
            'Position',
            'Serial No',
            'Previous Serial No',
            'Part No',
            'Action',
            'In Use',
            'Quantity',
            'Cost (RM)',
            'Remark',
          ],
          widths: [10, 35, 35, 12, 8],
        },
        {
          title: 'Documents',
          rows: review.Documents || [],
          columns: ['Ref No', 'Type', 'Code', 'Date', 'Remark'],
          widths: [10, 9, 8, 12, 50, 11],
        },
        {
          title: 'Approval History',
          rows: [...(review.History || [])]
            .reverse()
            .sort((a, b) => (Number(b.Round) || 0) - (Number(a.Round) || 0) || new Date(b.Date) - new Date(a.Date)),
          columns: ['Status', 'UserID', 'Date', 'Amount', 'Remark'],
          widths: [10, 12, 17, 15, 40],
        },
      ];
      return sections
        .map((section) => {
          const all = section.rows.map((row) => ({
            ...row,
            Change: section.comparable ? this.childChange(row, baseline) : '',
          }));
          let rows =
            section.comparable && baseline && this.showReviewChangesOnly ? all.filter((row) => !!row.Change) : all;
          const changedCount = rows.length;
          const numbers = new Map();
          rows = rows.map((row, index) => {
            const group = String(row['Service Code'] || '').trim();
            const number = section.title === 'Tasks' ? (numbers.get(group) || 0) + 1 : index + 1;
            numbers.set(group, number);
            return { ...row, '#': number };
          });
          const columns = [
            '#',
            ...section.columns.filter(
              (column) => column !== 'Change' || rows.some((row) => row.Change === 'New' || row.Change === 'Edited'),
            ),
          ];
          const tableSection = { ...section, columns };
          return {
            ...tableSection,
            heading:
              section.title === 'Approval History' ?
                section.title
              : section.title +
                ' (' +
                (section.comparable && baseline && this.showReviewChangesOnly ? rows.length + ' of ' : '') +
                all.length +
                ')',
            rows,
            tableRows: rows.map((row, index) => this.reviewTableRow(tableSection, row, index)),
            dataType: Object.fromEntries(
              columns
                .filter((column) => column !== 'File')
                .map((column) => [
                  this.reviewColumnLabel(column, section.title),
                  column === 'Cost (RM)' ? 'currency'
                  : ['#', 'Quantity', 'Round'].includes(column) ? 'number'
                  : 'string',
                ]),
            ),
            count: all.length,
            changedCount,
            hidden: all.length - rows.length,
            total: rows.reduce((sum, row) => sum + this.amountNumber(row['Cost (RM)']), 0),
          };
        })
        .filter((section) => section.rows.length > 0);
    },
    isAllDocTypes: function () {
      return !!this.selectedDocType && this.selectedDocType['Code'] === 'ALL';
    },
    isWorkOrder: function () {
      return !!this.selectedDocType && this.selectedDocType['Code'] === 'EMSJOB';
    },
    pendingFilterChips: function () {
      const chips = [];
      const docType = this.selectedDocType?.Code;
      if (docType && docType !== 'ALL') {
        chips.push({
          key: 'docType',
          label: 'Document Type',
          value: docType === 'EMSJOB' ? 'Work Order' : docType,
        });
      }
      if (this.selectedStatus && this.selectedStatus['Status'] !== 'All') {
        chips.push({ key: 'status', label: 'Status', value: this.selectedStatus['Status'] });
      }
      if (this.selectedIssuedTo.length) {
        chips.push({ key: 'issuedTo', label: 'Issued To', value: this.selectedIssuedTo.map((item) => item['Issued To']).join(', ') });
      }
      if (!this.isWorkOrder && this.selectedPaymentType) {
        chips.push({ key: 'paymentType', label: 'Payment Type', value: this.selectedPaymentType['Payment Type'] });
      }
      if (this.createdby && this.createdby.trim()) {
        chips.push({ key: 'createdBy', label: 'Requested By', value: this.createdby.trim() });
      }
      if (this.selectedChargesCode.length) {
        chips.push({
          key: 'chargesCode',
          label: 'Charges Code',
          value: this.selectedChargesCode.map((c) => c['Charge Code']).join(', '),
        });
      }
      if (this.amount && (this.amount !== '0' || this.amountOperationIndex !== 0)) {
        chips.push({ key: 'amount', label: 'Amount', value: `${this.amountOperation} ${this.amount}` });
      }
      if (this.isWorkOrder || this.isAllDocTypes) {
        if (this.workOrderFilters.vehicle.length)
          chips.push({ key: 'workOrder.vehicle', label: 'Vehicle', value: this.workOrderFilters.vehicle.map((vehicle) => vehicle.label).join(', ') });
        for (const [key, label, field] of [['categories', 'Vehicle Category', 'Vehicle Category'], ['teams', 'Team', 'Team']]) {
          if (this.workOrderFilters[key].length)
            chips.push({ key: `workOrder.${key}`, label, value: this.workOrderFilters[key].map((item) => item.label || item[field]).join(', ') });
        }
      }
      return chips;
    },
    amountOperation: function () {
      return this.amountOperations[this.amountOperationIndex].symbol;
    },
    fixWidth: function () {
      let width = '0px';
      if (this.closeLeftTab && !this.narrowWindow) {
        width = '40px';
      } else if (this.closeLeftTab && this.narrowWindow) {
        width = '0px';
      } else if (!this.closeLeftTab && !this.narrowWindow) {
        width = '250px';
      } else if (!this.closeLeftTab && this.narrowWindow) {
        width = '0px';
      }
      return `calc(100% - ${width})`;
    },
    fixHeight: function () {
      let height = '0px';
      if (this.closeFilterMesgTab && !this.narrowWindow) {
        height = '35px';
      } else if (!this.closeFilterMesgTab && !this.narrowWindow) {
        height = '140px';
      }
      return `calc(100% - ${height})`;
    },
  },
  methods: {
    documentCurrency(doc) {
      return String(
        this.isWorkOrderDoc(doc) ?
          this.companyCurrency || doc.Currency || ''
        : doc.Currency || this.companyCurrency || '',
      ).trim();
    },
    reviewTyrePositionLabel(value) {
      const index = String(value ?? '').trim();
      const position = this.reviewTyrePositions.find((item) => {
        const configured = String(item.index).trim();
        return configured === index || (index !== '' && Number(configured) === Number(index));
      });
      if (position?.description) return position.description;
      if (index === '0C') return 'Front Tyre';
      if (index === '1C') return 'Rear Tyre';
      const number = Number(index);
      if (index !== '' && Number.isInteger(number) && number >= 100 && number <= 103) return 'Spare' + (number - 99);
      if (index !== '' && Number.isInteger(number) && number >= 0 && number < 100)
        return (
          (number < 50 ? 'Left' : 'Right') + ' Column ' + (Math.floor(number / 10) + 1) + ' Row ' + ((number % 10) + 1)
        );
      return 'Unknown position';
    },
    reviewColumnLabel(column, sectionTitle) {
      if (column === 'Change') return 'Change';
      if (sectionTitle === 'Tyre Changes')
        return (
          {
            Position: 'Tyre Position',
            'Serial No': 'New Serial No',
            'Previous Serial No': 'Old Serial No',
            Action: 'Replace Type',
            'Cost (RM)': 'Cost (' + this.reviewCurrency + ')',
          }[column] || column
        );
      return { 'Cost (RM)': 'Cost (' + this.reviewCurrency + ')', UserID: 'User' }[column] || column;
    },
    escapeReviewText(value) {
      return String(value ?? '').replace(
        /[&<>"']/g,
        (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char],
      );
    },
    reviewTableRow(section, row, index) {
      const display = {};
      const dateTooltip = section.comparable ? this.childDateTooltip(row) : '';
      section.columns
        .filter((column) => column !== 'File')
        .forEach((column) => {
          const key = this.reviewColumnLabel(column, section.title);
          if (row[column] == null || String(row[column]).trim() === '') { display[key] = ''; return; }
          if (['#', 'Quantity', 'Cost (RM)', 'Round'].includes(column)) {
            display[key] = this.amountNumber(row[column]);
            return;
          }
          const text =
            column === 'Change' ? row.Change
            : column === 'Amount' ? this.reviewCurrency + ' ' + this.$currencyFormat(this.amountNumber(row[column]))
            : section.title === 'Tasks' && ['Date Start', 'Date End'].includes(column) ? row[column]
            : this.reviewValue(row[column], column === 'Date' && section.title === 'Documents' ? 'Date Only' : column);
          const tooltip = [
            column === 'Task Description' ? row.Remark
            : column === 'Service' ? row.Service
            : '',
            dateTooltip,
          ]
            .filter(Boolean)
            .join('\n');
          const className =
            column === 'Change' ? 'status-chip status-chip--' + (row.Change === 'New' ? 'ok' : 'warn')
            : column === 'Status' && ['Submitted', 'Rejected', 'Approved'].includes(row.Status) ?
              'status-chip status-chip--' + ({ Submitted: 'warn', Approved: 'ok', Rejected: 'bad' })[row.Status]
            : column === 'Remark' ? 'review-wrap'
            : column === 'Amount' ? 'review-amount'
            : '';
          display[key] =
            `<span class="${className}" title="${this.escapeReviewText(tooltip)}">${this.escapeReviewText(text)}</span>`;
        });
      display._ReviewIndex = index;
      if (section.title === 'Tasks') display._ServiceGroup = this.escapeReviewText(row.Service ? 'Service: ' + row.Service : '');
      return display;
    },
    reviewDocumentUnavailable(row) {
      return !String(this.reviewDocumentsSection?.rows[row?._ReviewIndex]?.PicDocFile || '').trim();
    },
    reviewTableButtonClicked(section, text, row) {
      if (text !== 'View file') return;
      const document = section.rows[row._ReviewIndex];
      if (String(document?.PicDocFile || '').trim()) this.viewWorkOrderDocument(document);
      else this.$showAlert('warning', 'Document', 'This file is unavailable.');
    },
    async loadCompanyCurrency() {
      const companyDetails = await this.callApprovalApi('/Master/GetDetailsMaster', {
        masterfile: 'COMPANY',
        module: 'MASTER',
        recordid: this.$store.state.loginDetails.CoCode,
      });
      const company = companyDetails.Company || companyDetails;
      const record = Array.isArray(company) ? company[0] : company;
      this.companyCurrency = String(record?.CompanyLocalCur || record?.CoLocalCur || record?.co_localcur || '').trim();
    },
    isApprovedBucket(bucket) {
      return (
        String(bucket.Bucket || '')
          .trim()
          .toUpperCase() === 'APPROVED IN LAST 7 DAYS'
      );
    },
    canSelectDocument(doc) {
      if (this.approvalSetupMissing) return false;
      if (this.isWorkOrderDoc(doc)) {
        const canApprove = doc.CanApprove == null ? String(doc.Assigned).trim() === '1' : ['true', '1'].includes(String(doc.CanApprove).trim().toLowerCase());
        return String(doc['Approval Status'] || '').trim() === 'Pending' && canApprove;
      }
      return !this.isRejectedDocument(doc) && String(doc.Assigned) !== '0';
    },
    isRejectedDocument(doc) {
      const status = String(doc['Approval Status'] || '').trim();
      if (status) return status === 'Rejected';
      const submitted = new Date(doc['Submitted Date'] || '').getTime();
      const rejected = new Date(doc['Rejected Date'] || '').getTime();
      if (Number.isFinite(submitted) && Number.isFinite(rejected) && submitted > rejected) return false;
      return String(doc.APStatus || '').trim() === 'R';
    },
    documentKey(doc) {
      return (doc['Document Type Code'] || doc['Document Type']) + ':' + String(doc['Document No'] || '').trim();
    },
    documentSourceIndex(doc) {
      return this.buckets.findIndex((bucket) => bucket.Documents.includes(doc));
    },
    sourceDocumentIndex(doc) {
      const bucket = this.buckets[this.documentSourceIndex(doc)];
      return bucket ? bucket.Documents.indexOf(doc) : -1;
    },
    approvalStatusLabel(status) {
      return status === 'Approved' ? 'Fully approved' : status || '-';
    },
    formatDocumentDate(value, includeTime = false) {
      const date = new Date(value);
      return !value || Number.isNaN(date.getTime()) ? '-' : date.formatString(includeTime ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd');
    },
    amountNumber(value) {
      if (value == null || value === '') return 0;
      const number =
        typeof value === 'number' ? value
        : this.$currencyToNumber ? this.$currencyToNumber(value)
        : Number(value);
      return Number.isFinite(Number(number)) ? Number(number) : 0;
    },
    comparisonBaseline(review) {
      if (!review) return null;
      const history = review.History || [];
      if (review.Header?.LastApprovalDate) {
        const record = history.find(
          (row) =>
            row.Status === 'Approved' &&
            new Date(row.Date).getTime() === new Date(review.Header.LastApprovalDate).getTime(),
        );
        return {
          kind: 'Approval',
          date: review.Header.LastApprovalDate,
          amount: record ? record.Amount : review.Header.ApprovedAmount,
        };
      }
      return (
        history
          .filter(
            (row) =>
              row.Status === 'Rejected' &&
              Number(row.Round) < Math.max(0, ...history.map((item) => Number(item.Round) || 0)),
          )
          .map((row) => ({
            kind: 'Rejected',
            date: row.Date,
            amount: row.Amount,
            user: row.UserID,
            remark: row.Remark,
          }))
          .filter((row) => row.date && !Number.isNaN(new Date(row.date).getTime()))
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null
      );
    },
    childChange(row, baseline) {
      if (!baseline) return '';
      const date = (value) => (value && !Number.isNaN(new Date(value).getTime()) ? new Date(value).getTime() : null);
      const created = date(row['Date Created']),
        updated = date(row['Date Updated']),
        cutoff = new Date(baseline.date).getTime();
      if (created != null && created > cutoff) return 'New';
      if (updated != null && updated > cutoff) return 'Edited';
      if (baseline.kind === 'Approval') {
        if (row['After Approval'] === 'Added after approval') return 'New';
        if (row['After Approval'] === 'Updated after approval') return 'Edited';
      }
      return '';
    },
    childDateTooltip(row) {
      return (
        'Created: ' +
        this.reviewValue(row['Date Created'], 'Date') +
        '\nUpdated: ' +
        this.reviewValue(row['Date Updated'], 'Date')
      );
    },
    changeSummary(review) {
      if (!review) return '-';
      const baseline = this.comparisonBaseline(review);
      if (!baseline)
        return (review.History || []).some((row) => row.Status === 'Rejected' || row.Status === 'Approved') ?
            'Comparison unavailable'
          : 'First submission';
      const summary = ['Tasks', 'Parts'].flatMap((key) => {
        const changes = (review[key] || []).map((row) => this.childChange(row, baseline));
        return ['New', 'Edited'].map((kind) => {
          const count = changes.filter((change) => change === kind).length;
          const label = key === 'Tasks' ? 'task' : 'part';
          return count ? (kind === 'New' ? '+' : '') + count + ' ' + label + (count === 1 ? '' : 's') + (kind === 'Edited' ? ' edited' : '') : '';
        }).filter(Boolean);
      });
      return (summary.join(', ') || 'No recorded changes') + (baseline.kind === 'Rejected' ? ' since rejection' : ' since full approval');
    },
    reviewWasDisplayed(doc) {
      const number = String(doc['Document No'] || '').trim();
      return (
        !!this.displayedWorkOrderReviews[number] &&
        this.displayedWorkOrderReviews[number].token === this.workOrderReviews[number]?.ReviewToken
      );
    },
    decisionDetails(doc) {
      const job = this.isWorkOrderDoc(doc);
      const fields = [
        {
          label: job ? 'Vehicle' : 'Issued To',
          value: job ? [doc['Vehicle #'], doc['Vehicle Code']].filter(Boolean).join(' - ') : doc['Issued To'],
        },
      ];
      if (!job) fields.push({ label: 'Shpr/Csgn', value: doc['shipper/consignee'] });
      fields.push(
        { label: job ? 'Job Date' : 'Issued Date', value: this.formatDocumentDate(doc['Date Issued']) },
        { label: 'Submitted', value: doc['Submitted Date'] ? this.formatDocumentDate(doc['Submitted Date'], true) : 'Not recorded' },
        { label: 'Bucket date', value: this.formatDocumentDate(doc['Bucket Date'], true) },
        { label: 'Requested by', value: job ? doc.Requester || doc['User ID'] : doc['User ID'] },
        { label: 'Remarks', value: doc.Remark || '-' },
      );
      return fields.map((field) => ({
        ...field,
        value: field.value == null || field.value === '' ? '-' : field.value,
      }));
    },
    resetDecisionPopup() {
      this.Emessages = [];
      this.decisionRequiredErrors = [];
      this.useSharedRemark = false;
      this.sharedDecisionRemark = '';
      this.selectedCheckedDocument.forEach((doc) => this.$set(doc, 'documents', []));
    },
    focusDecisionDocument(index) {
      const element = this.$refs['decision-document-' + index];
      const target = this.useSharedRemark ? this.$refs.sharedDecisionRemarkInput?.$el : element?.[0];
      if (target) {
        target.scrollIntoView({ block: 'nearest' });
        target.querySelector('textarea')?.focus({ preventScroll: true });
      }
    },
    closeWorkOrderReview() {
      if (this.reviewDecisionBusy) return;
      this.workOrderReviewRequestId++;
      this.showWorkOrderReview = false;
    },
    reloadWorkOrderReview() {
      if (this.workOrderReviewDocument && !this.reviewDecisionBusy)
        return this.openWorkOrderReview(this.workOrderReviewDocument, true);
    },
    async decideFromReview(decision) {
      if (this.reviewDecisionBusy || !this.canDecideReview || !this.workOrderReview) return;
      this.reviewRemarkRequired = decision === 'Rejected' && !this.reviewDecisionRemark.trim();
      this.reviewDecisionError = this.reviewRemarkRequired ? 'Reject remark is required.' : '';
      if (this.reviewRemarkRequired) return;
      this.reviewDecisionBusy = true;
      try {
        const doc = { ...this.workOrderReviewDocument, approveremarks: this.reviewDecisionRemark };
        const result = await this.approveDocument(doc, decision, this.workOrderReview);
        if (!result.success) {
          this.reviewDecisionError = result.message;
          return;
        }
        this.$delete(this.displayedWorkOrderReviews, doc['Document No'].trim());
        this.showWorkOrderReview = false;
        if (!this.selectedCheckedDocument.length) this.showApprovePopup = false;
        this.showApprovalResult('success', decision + ' Work Order', result.message);
      } catch (error) {
        this.reviewDecisionError = error.message || 'Unable to complete the decision.';
      } finally {
        this.reviewDecisionBusy = false;
      }
    },
    reviewDateTime(date, time) {
      if (!date) return '-';
      return this.reviewValue(date, 'Date Only') + (time ? ' ' + this.reviewValue(time, 'Time Start') : '');
    },
    reviewValue(value, column) {
      if (value == null || value === '') return '-';
      if (column === 'Amount' || column === 'Cost (RM)') return this.$currencyFormat(value);
      if (column.startsWith('Time ') && /^\d{4}$/.test(String(value).trim())) {
        const time = String(value).trim();
        return time.slice(0, 2) + ':' + time.slice(2);
      }
      if (column.includes('Date')) {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? value : (
            date.formatString(
              ['Date Only', 'Date Start', 'Date End'].includes(column) ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm',
            )
          );
      }
      return typeof value === 'string' ? value.trim() : value;
    },
    async loadWorkOrderReview(doc) {
      const result = await this.callApprovalApi('/DocumentApproval/DocApprovalWorkOrder', {
        actionflag: 'DETAILS',
        paymentno: doc['Document No'].trim(),
      });
      if (!result?.WorkOrderReview?.ReviewToken)
        throw new Error(result?.[0]?.ErrMsg || 'Unable to load saved Work Order details.');
      const review = result.WorkOrderReview;
      this.$set(this.workOrderReviews, doc['Document No'].trim(), review);
      if (review.AuditWarning) this.showReviewChangesOnly = false;
      return review;
    },
    async loadReviewTyreLayout(review, requestId) {
      if (!review.Tyres?.length || !review.Header.InventoryNo) return;
      this.reviewTyreLayoutLoading = true;
      try {
        const vehicleResult = await this.callApprovalApi('/Master/GetDetailsMaster', {
          masterfile: 'VEHICLE',
          recordid: review.Header.InventoryNo,
        });
        if (requestId !== this.workOrderReviewRequestId) return;
        const record = vehicleResult?.Vehicle;
        const vehicle = Array.isArray(record) ? record[0] : record;
        if (!vehicle?.VehicleCategory) return;
        const positions = await this.callApprovalApi(
          '/Vehicle/GetVehicleTyreConfiguration?category=' +
            encodeURIComponent(String(vehicle.VehicleCategory).trim()) +
            '&type=' +
            encodeURIComponent(String(vehicle.Type || '').trim()),
        );
        if (requestId !== this.workOrderReviewRequestId || !Array.isArray(positions) || !positions.length) return;
        this.reviewTyrePositions = [
          ...positions.map((position) => ({
            index: position.tyreindex,
            description: String(position.tyrenaming || '').trim(),
          })),
          ...[100, 101, 102, 103].map((index, number) => ({
            index: String(index),
            description: 'Spare' + (number + 1),
          })),
        ];
      } catch (error) {
        // The saved tyre table remains available if the optional vehicle layout cannot be loaded.
      } finally {
        if (requestId === this.workOrderReviewRequestId) this.reviewTyreLayoutLoading = false;
      }
    },
    async openWorkOrderReview(doc, preserveRemark = false, fromDecisionPopup = false) {
      if (!preserveRemark) this.reviewFromDecisionPopup = fromDecisionPopup;
      const requestId = ++this.workOrderReviewRequestId;
      this.workOrderReviewDocument = doc;
      this.showWorkOrderReview = true;
      this.workOrderReview = null;
      this.workOrderReviewError = '';
      this.workOrderReviewLoading = true;
      this.reviewDecisionError = '';
      this.reviewRemarkRequired = false;
      this.showReviewChangesOnly = false;
      this.reviewTyrePositions = [];
      this.reviewTyreLayoutLoading = false;
      if (!preserveRemark) this.reviewDecisionRemark = doc.approveremarks || '';
      this.$delete(this.displayedWorkOrderReviews, doc['Document No'].trim());
      try {
        const review = await this.loadWorkOrderReview(doc);
        if (requestId !== this.workOrderReviewRequestId) return;
        this.workOrderReview = review;
        this.loadReviewTyreLayout(review, requestId);
        this.$set(this.displayedWorkOrderReviews, doc['Document No'].trim(), {
          token: review.ReviewToken,
        });
      } catch (error) {
        if (requestId === this.workOrderReviewRequestId)
          this.workOrderReviewError = error.message || 'Unable to load Work Order details.';
      } finally {
        if (requestId === this.workOrderReviewRequestId) this.workOrderReviewLoading = false;
      }
    },
    viewWorkOrderDocument(doc) {
      const storedType = (doc.PicDocType || '').trim();
      const type =
        storedType === 'application/pdf' ? '.pdf'
        : storedType === 'audio/ogg' ? '.ogg'
        : storedType;
      const mimeTypes = {
        '.pdf': 'application/pdf',
        '.ogg': 'audio/ogg',
        '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        '.png': 'image/png',
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.gif': 'image/gif',
        '.bmp': 'image/bmp',
      };
      const mime =
        mimeTypes[type.toLowerCase()] || (type.toLowerCase().startsWith('image/') ? type : 'application/octet-stream');
      this.attachmentList = [
        {
          AttImage: `data:${mime};base64,${doc.PicDocFile}`,
          AttImageType: type || 'n/a',
          'Job No': this.workOrderReview.Header.JobNo,
          Status: doc['Ref No'] || 'Document',
          Remarks: doc.Remark || '',
          'Document Date': this.reviewValue(doc.Date, 'Date Only'),
        },
      ];
      this.attAdditionalDetails = [];
      this.attToView = this.attachmentList[0];
      this.returnToWorkOrderReview = true;
      this.showAttachmentPopup = true;
    },
    async callApprovalApi(endpoint, data, action = '') {
      const record = (status, message) => {
        if (!action) return;
        const outcome = status === 'Success' ? ({ Approve: 'Approved', Reject: 'Rejected', 'Cancel Approval': 'Approval cancelled' }[action] || `${action} succeeded`) : `${action} failed`;
        this.apiMessages.unshift({ id: ++this.apiMessageId, status, message, outcome,
          date: new Date().formatString('yyyy-MM-dd HH:mm:ss'), documentNo: String(data?.paymentno || data?.fileno || '').trim() });
        if (this.apiMessages.length > 100) this.apiMessages.pop();
      };
      try {
        const result = await this.$apiCallV2(endpoint, data);
        const response = Array.isArray(result) ? result[0] : result;
        const message = String(response?.ErrMsg || '').trim();
        record(response?.Results != null && String(response.Results) !== '0' ? 'Error' : 'Success', message);
        return result;
      } catch (error) {
        record('Error', String(error.message || '').trim());
        throw error;
      }
    },
    showApprovalResult: function (type, title, message) {
      const escapedMessage = message
        .trim()
        .replace(/[&<>]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[char]);
      this.$showAlert(type, title, escapedMessage);
    },
    selectDocType: function (t) {
      t = this.availableDocTypes.find((type) => type.Code === t.Code) || this.availableDocTypes[0];
      if (!t) return;
      if (t.Code === 'EMSJOB' && !this.workOrderApprovalEnabled) return;
      this.selectedDocType = t;
      if (t['Code'] === 'EMSJOB') {
        this.selectedIssuedTo = [];
        this.selectedPaymentType = null;
        this.selectedChargesCode = [];
      }
      this.filterData();
    },
    isWorkOrderDoc: function (d) {
      return ['WorkOrder', 'EMSJOB'].includes((d['Document Type'] || '').replace(/\s/g, ''));
    },
    docTypeLabel: function (d) {
      return this.isWorkOrderDoc(d) ? 'Work Order' : d['Document Type'] || 'Payment';
    },
    docTagClass: function (d) {
      if (this.isWorkOrderDoc(d)) return 'doc-tag--workorder';
      return d['Document Type'] === 'Cheque' ? 'doc-tag--cheque' : 'doc-tag--payment';
    },
    refreshClicked: async function () {
      if (this.approvalSetupMissing) return;
      this.refreshingCount++;
      try {
        await this.loadWorkOrderApprovalSettings();
        await this.callAPI();
        this.filterData();
        this.documentCheckedCount();
      } finally {
        this.refreshingCount--;
      }
    },
    async loadAmountPreference() {
      try {
        const rows = await this.callApprovalApi('/UserOptions/GetSettingsSystemUser?settings=DA_AmountFilter');
        if (rows?.[0]?.Results != null && String(rows[0].Results) !== '0') throw new Error(rows[0].ErrMsg || 'Unable to load your saved amount filter.');
        const value = rows?.[0]?.SettingsValue ? JSON.parse(rows[0].SettingsValue) : { remember: false };
        this.amountPreferenceLoaded = true;
        this.savedAmountPreference = JSON.stringify({ remember: false });
        if (value.remember === false) return;
        const index = this.amountOperations.findIndex((operation) => (operation.code === value.operator || operation.symbol === value.operator));
        if (index < 0 || !['string', 'number'].includes(typeof value.amount) || !Number.isFinite(Number(value.amount))) return;
        this.amountPreferenceEnabled = true;
        this.amount = String(value.amount);
        this.amountOperationIndex = index;
        this.savedAmountPreference = JSON.stringify({ remember: true, amount: this.amount, operator: this.amountOperations[index].code });
      } catch (error) {
        this.amountPreferenceEnabled = false;
        this.showApprovalResult('warning', 'Amount filter not restored', error.message || 'Unable to load your saved amount filter.');
      }
    },
    async saveAmountPreference() {
      if (this.approvalSetupMissing || !this.amountPreferenceLoaded || this.amountPreferenceSaving) return;
      const value = this.amountPreference;
      const saved = JSON.stringify(value);
      if (saved === this.savedAmountPreference) return;
      if (value.remember && !Number.isFinite(Number(value.amount))) {
        this.showApprovalResult('warning', 'Amount filter not saved', 'Enter a numeric amount to save the amount filter.');
        return;
      }
      this.amountPreferenceSaving = true;
      try {
        const result = await this.callApprovalApi('/UserOptions/SaveSettingsSystemUser', { item: 'DA_AmountFilter', value }, 'Save amount filter');
        if (String(result?.[0]?.Results) !== '0') throw new Error(result?.[0]?.ErrMsg || 'Unable to save your amount filter.');
        this.savedAmountPreference = saved;
      } catch (error) {
        this.showApprovalResult('warning', 'Amount filter not saved', error.message || 'Unable to save your amount filter.');
      } finally {
        this.amountPreferenceSaving = false;
        if (!this._isDestroyed && JSON.stringify(this.amountPreference) !== saved) {
          clearTimeout(this.amountPreferenceTimer);
          this.amountPreferenceTimer = setTimeout(() => this.saveAmountPreference(), 500);
        }
      }
    },
    loadWorkOrderApprovalSettings: async function () {
      try {
        const result = await this.callApprovalApi('/DocumentApproval/DocApprovalWorkOrder', { actionflag: 'SETTINGS' });
        if (!result?.WorkOrderSettings) return;
        this.workOrderApprovalEnabled = result.WorkOrderSettings.Enabled === true;
        this.workOrderApprovalAccess = !this.workOrderApprovalEnabled ? false : typeof result.WorkOrderSettings.CanView === 'boolean' ? result.WorkOrderSettings.CanView : null;
        if (!this.workOrderApprovalEnabled) {
          if (this.isWorkOrder) this.selectedDocType = this.docType[0];
          this.workOrderFilters = { vehicle: [], categories: [], teams: [] };
        }
      } catch (error) {
        /* Retain the last successfully loaded setting. */
      }
    },

    clearFilter: function (key) {
      if (key.startsWith('workOrder.')) {
        const field = key.split('.')[1];
        this.workOrderFilters[field] = [];
        return;
      }
      switch (key) {
        case 'docType':
          this.selectDocType(this.docType[0]);
          break;
        case 'status':
          this.selectedStatus = this.statusType[0];
          break;
        case 'issuedTo':
          this.selectedIssuedTo = [];
          break;
        case 'paymentType':
          this.selectedPaymentType = null;
          break;
        case 'createdBy':
          this.createdby = '';
          break;
        case 'chargesCode':
          this.selectedChargesCode = [];
          break;
        case 'amount':
          this.amount = '';
          this.amountOperationIndex = 0;
          break;
      }
    },
    clearAllFilters: function () {
      this.resetFilterClick();
      this.selectedStatus = this.statusType[0];
    },
    updateDocTypeCounts: function () {
      // Pending documents currently loaded (limited by "Show Top"), counted per document type.
      const counts = { Payment: 0, Cheque: 0, EMSJOB: 0 };
      this.buckets
        .filter((b) => (b['Bucket'] || '').split(' ')[0] == 'Pending')
        .forEach((b) => {
          (b['Documents'] || []).forEach((d) => {
            if (this.isRejectedDocument(d)) return;
            const code =
              this.isWorkOrderDoc(d) ? 'EMSJOB'
              : d['Document Type'] === 'Cheque' ? 'Cheque'
              : 'Payment';
            counts[code]++;
          });
        });
      const next = { ...this.docTypeCounts };
      if (this.refreshDocType === 'ALL') {
        Object.assign(next, counts);
      } else if (this.refreshDocType) {
        next[this.refreshDocType] = counts[this.refreshDocType];
      }
      if (['Payment', 'Cheque', 'EMSJOB'].every((c) => next[c] !== undefined)) {
        next['ALL'] = next['Payment'] + next['Cheque'] + next['EMSJOB'];
      }
      this.docTypeCounts = next;
    },
    resetFilterClick: function () {
      this.workOrderFilters = { vehicle: [], categories: [], teams: [] };
      this.selectedIssuedTo = [];
      this.selectedPaymentType = null;
      this.createdby = '';
      this.selectedChargesCode = [];
      if (!this.amountPreferenceEnabled) {
        this.amount = '';
        this.amountOperationIndex = 0;
      }
    },

    callAPI: async function () {
      const requestId = ++this.refreshRequestId;
      const requestedType = 'ALL';
      const refreshErrors = [];
      this.getDocApprovalPromises = [];

      for (let b of this.buckets) {
        b['bucketChecked'] = false;

        var postData = {
          // "tablename": this.selectedDocuments['Code'],
          tablename: requestedType,
          // 'authorized': 'C',
          authorized: b['Bucket'].split(' ')[0] == 'Pending' ? 'U' : 'A',
          actionflag: '',
          paymentno: '',
          txnid: '',
          startdate: '',
          enddate: '',
          usercreate: '',
          // 'bucket': this.bucketDetails[i]['Bucket'],
          bucket: b['Bucket'],
          startusercreate: '',
          endusercreate: '',
          docatt: '',
          attfile: '',
          filetype: '',
          remarks: '',
          top: this.selectedTop ? this.selectedTop['top'] : '',
        };

        const requests = [{ url: '/DocumentApproval/getDoc', body: postData }];
        if (postData.tablename === 'EMSJOB') {
          requests[0].url = '/DocumentApproval/DocApprovalWorkOrder';
        } else if (postData.tablename === 'ALL') {
          requests.splice(0, 1, ...['Payment', 'Cheque'].map((tablename) => ({ url: '/DocumentApproval/getDoc', body: { ...postData, tablename } })));
          if (this.workOrderApprovalEnabled) requests.push({ url: '/DocumentApproval/DocApprovalWorkOrder', body: { ...postData, tablename: 'EMSJOB' } });
        }
        this.getDocApprovalPromises.push(
          Promise.allSettled(requests.map((request) => this.callApprovalApi(request.url, request.body))).then((results) => {
            if (requestId !== this.refreshRequestId) return;
            let docs = [];
            this.$set(b, 'total', 0);
            if (!b.approvalSources) this.$set(b, 'approvalSources', {});
            results.forEach((result, index) => {
              const request = requests[index];
              const sourceKey = `${request.url}:${request.body.tablename}:${request.body.top}`;
              // The legacy Payment / Cheque endpoint returns [] for an empty bucket.
              const emptyLegacyBucket =
                result.status === 'fulfilled' &&
                request.url === '/DocumentApproval/getDoc' &&
                Array.isArray(result.value) &&
                result.value.length === 0;
              const noApprovalAccess =
                result.status === 'fulfilled' &&
                request.url === '/DocumentApproval/getDoc' &&
                ['Cannot view document. User not an authorizer.', 'Cannot get Authorized Level details.', 'Document type provided is not used for document approval.'].includes(String(result.value?.[0]?.ErrMsg || '').trim());
              const approval =
                emptyLegacyBucket || noApprovalAccess ?
                  { DocList: [], total: 0 }
                : result.status === 'fulfilled' && result.value?.DocApproval;
              if (approval && Array.isArray(approval.DocList)) {
                if (request.url === '/DocumentApproval/getDoc') this.$set(this.legacyApprovalAccess, request.body.tablename, !noApprovalAccess);
                approval.DocList.forEach((doc) => {
                  doc.show = true;
                  doc.checked = false;
                });
                this.$set(b.approvalSources, sourceKey, {
                  documents: approval.DocList,
                  total: Number(approval.total) || 0,
                });
              } else {
                const type = request.body.tablename === 'EMSJOB' ? 'Work Orders' : 'Payment / Cheque';
                const message = result.status === 'fulfilled' ? result.value?.[0]?.ErrMsg : result.reason?.message;
                refreshErrors.push(`${type}: ${message || 'Unable to refresh.'}`);
              }
              const source = b.approvalSources[sourceKey];
              if (source) {
                b['total'] += source.total;
                docs.push(...source.documents);
              }
            });
            docs.sort((a, b) => new Date(b['Date Issued']) - new Date(a['Date Issued']));
            docs.forEach((d) => {
              d['show'] = true;
              if (typeof d['checked'] !== 'boolean') d['checked'] = false;
              this.$set(d, 'approveremarks', d.approveremarks || '');
              this.$set(d, 'documentremarks', d.documentremarks || '');
              this.$set(d, 'documents', d.documents || []);
            });

            if (docs) {
              docs.forEach((a) => {
                if (a['AdditionalDetails']) {
                  if (a['AdditionalDetails'].length > 0) {
                    a['TotalOffsetAmt'] = '0';
                    a['TotalAdjustAmt'] = '0';
                    a['TotalNetAmt'] = '0';
                    a['AdditionalDetails'].forEach((b) => {
                      a['TotalOffsetAmt'] = (
                        +a['TotalOffsetAmt'] + (b['OffsetAmt'] ? +b['OffsetAmt'] : +'0.00')
                      ).toFixed(2);
                      a['TotalAdjustAmt'] = (
                        +a['TotalAdjustAmt'] + (b['AdjustAmt'] ? +b['AdjustAmt'] : +'0.00')
                      ).toFixed(2);
                      a['TotalNetAmt'] = (+a['TotalNetAmt'] + (b['NetAmt'] ? +b['NetAmt'] : +'0.00')).toFixed(2);
                    });
                  }
                }
              });
            }

            this.$set(b, 'Documents', docs);
          }),
        );
      }

      await Promise.allSettled(this.getDocApprovalPromises);
      if (requestId !== this.refreshRequestId) return;
      this.filterData();
      if (!this.availableDocTypes.some((type) => type.Code === this.selectedDocType.Code)) this.selectedDocType = this.availableDocTypes[0] || this.docType[0];
      this.refreshError =
        refreshErrors.length ?
          'Refresh incomplete. Previously loaded records are retained for failed lists. ' +
          [...new Set(refreshErrors)].join(' ')
        : '';
      if (!refreshErrors.length) this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm:ss');
      this.refreshDocType = requestedType;
      this.updateDocTypeCounts();
    },

    quickApprovedClicked: async function (decision = 'Approved') {
      if (this.approvalSetupMissing) return;
      this.loading = true;
      // Native click events must always mean Approve; only the decision popup passes Reject.
      let Request = decision === 'Rejected' ? 'Rejected' : 'Approved';
      this.loadText = Request === 'Rejected' ? 'Rejecting Documents' : 'Approving Documents';
      let allChecked = [];
      let count = 0;
      let failed = 0;
      const results = [];
      if (this.selectedCheckedDocument.length != 0) {
        const documents = this.selectedCheckedDocument.slice();
        for (let b of documents) {
          const decisionDoc =
            this.showApprovePopup && this.useSharedRemark ? { ...b, approveremarks: this.sharedDecisionRemark } : b;
          const result = await this.approveDocument(decisionDoc, Request);
          if (!result.success) failed++;
          results.push(result.message);
          this.loadText = `${Request} Documents ${++count} of ${documents.length}`;
        }

        this.loading = false;
        this.showApprovalResult(failed ? 'warning' : 'success', `${Request} Documents`, results.join('\n'));
      } else {
        this.$showAlert('warning', 'No Documents Selected', 'Please select a document before making an approval.');
      }
      // this.buckets.data.forEach(b => {
      //   allChecked = allChecked.concat(b['Documents'].filter(d => d['checked']));
      // });
      // allChecked.forEach((a, ai) => {
      //   console.log(a);
      //   this.approveDocument(a);
      // });
      this.loading = false;
    },

    applyApprovalDecision(doc, decision, state = {}) {
      const key = this.documentKey(doc);
      const workOrder = this.isWorkOrderDoc(doc);
      const source = this.buckets.find((bucket) => !this.isApprovedBucket(bucket) && bucket.Documents.some((item) => this.documentKey(item) === key));
      const original = source?.Documents.find((item) => this.documentKey(item) === key) || doc;
      const rejected = decision === 'Rejected';
      const date = state['Decision Date'] || new Date().toISOString();
      const updated = { ...original, ...state, checked: false, documents: [], CanApprove: false, Assigned: '0', 'Approved by me': !rejected };
      if (rejected) {
        updated.APStatus = 'R';
        updated['Approval Status'] = 'Rejected';
        updated.APRemark = state.APRemark || doc.approveremarks || '';
        updated['Rejected By'] = state['Rejected By'] || this.userid;
        updated['Rejected Date'] = state['Rejected Date'] || date;
        if (!workOrder) updated['Bucket Date'] = date;
      } else {
        updated['Bucket Date'] = date;
        updated['Rejected By'] = '';
        updated['Rejected Date'] = null;
      }
      this.$set(original, 'checked', false);
      this.buckets.forEach((bucket) => {
        if (bucket.Documents.some((item) => this.documentKey(item) === key)) {
          this.$set(bucket, 'total', Math.max(Number(bucket.total || 0), bucket.Documents.length) - 1);
          this.$set(bucket, 'Documents', bucket.Documents.filter((item) => this.documentKey(item) !== key));
        }
        Object.values(bucket.approvalSources || {}).forEach((cache) => {
          if (!cache.documents.some((item) => this.documentKey(item) === key)) return;
          cache.total = Math.max(Number(cache.total || 0), cache.documents.length) - 1;
          cache.documents = cache.documents.filter((item) => this.documentKey(item) !== key);
        });
      });
      const destination = rejected ? (workOrder ? source : this.buckets.find((bucket) => !this.isApprovedBucket(bucket) && bucket.StartDate != null && bucket.EndDate != null && Number(bucket.StartDate) <= 0 && Number(bucket.EndDate) >= 0) || this.buckets.find((bucket) => /^Pending Approval <= 10 days$/i.test(bucket.Bucket)) || source) : this.buckets.find((bucket) => this.isApprovedBucket(bucket));
      if (destination) {
        this.$set(destination, 'total', Math.max(Number(destination.total || 0), destination.Documents.length) + 1);
        destination.Documents.unshift(updated);
        if (!destination.approvalSources) this.$set(destination, 'approvalSources', {});
        const type = workOrder ? 'EMSJOB' : doc['Document Type'] === 'Cheque' ? 'Cheque' : 'Payment';
        const prefix = `${workOrder ? '/DocumentApproval/DocApprovalWorkOrder' : '/DocumentApproval/getDoc'}:${type}:`;
        const keys = Object.keys(destination.approvalSources).filter((sourceKey) => sourceKey.startsWith(prefix));
        if (!keys.length) {
          this.$set(destination.approvalSources, prefix + (this.selectedTop?.top || ''), { documents: [updated], total: 1 });
        } else {
          keys.forEach((sourceKey) => {
            const cache = destination.approvalSources[sourceKey];
            cache.total = Math.max(Number(cache.total || 0), cache.documents.length) + 1;
            cache.documents.unshift(updated);
          });
        }
      }
      this.$delete(this.displayedWorkOrderReviews, String(doc['Document No'] || '').trim());
      this.filterData();
      this.documentCheckedCount();
      this.updateDocTypeCounts();
    },
    approveDocument: async function (val, Req, displayedReview = null) {
      console.log(Req);
      let postApproval;
      try {
        var dataToPost = {
          tablename: val['Document Type Code'] || val['Document Type'],
          authorized:
            Req ?
              Req == 'Rejected' ?
                'R'
              : 'A'
            : 'A',
          actionflag: 'E',
          paymentno: val['Document No'].trim(),
          txnid: '',
          startdate: '',
          enddate: '',
          bucket: '',
          startusercreate: '',
          endusercreate: '',
          remarks: val['approveremarks'] ? val['approveremarks'] : '',
          documents:
            Req !== 'Rejected' && val['documents']?.length ?
              [
                {
                  docno: '',
                  doc: 'ATT',
                  docremarks: '',
                  remarks: val['documentremarks'] ? val['documentremarks'] : '',
                  docaction: 'N',
                  attachment: val['documents'] ? val['documents'] : null,
                },
              ]
            : [],
        };
        console.log(dataToPost);
        const approvalUrl =
          this.isWorkOrderDoc(val) ? '/DocumentApproval/DocApprovalWorkOrder' : '/DocumentApproval/approveDoc';
        if (this.isWorkOrderDoc(val)) {
          const number = val['Document No'].trim();
          const review = displayedReview || this.workOrderReviews[number] || (await this.loadWorkOrderReview(val));
          dataToPost.reviewtoken = review.ReviewToken;
          delete dataToPost.documents;
        }
        postApproval = await this.callApprovalApi(approvalUrl, dataToPost, dataToPost.authorized === 'R' ? 'Reject' : 'Approve');
        console.log(postApproval);
      } catch (e) {
        console.log(e);
        postApproval = [{ Results: '1', ErrMsg: e.message || 'Unable to load or approve the document.' }];
      }

      const resultPrefix =
        new Date().formatString('yyyy-MM-dd HH:mm:ss') +
        '| ' +
        val['Document Type'] +
        ' ' +
        val['Document No'].trim() +
        ' ' +
        Req.trim();
      if (postApproval && postApproval[0] && postApproval[0].Results == '0') {
        if (this.isWorkOrderDoc(val)) this.$delete(this.workOrderReviews, val['Document No'].trim());
        this.applyApprovalDecision(val, Req, postApproval[0].ApprovalState || {});
        return { success: true, message: resultPrefix + '.' };
      } else {
        const error = postApproval && postApproval[0] ? postApproval[0].ErrMsg : 'Unable to complete request';
        return { success: false, message: resultPrefix + ' failed. ' + error + '.' };
      }
    },

    cancelApproveDocument: async function (val) {
      try {
        var dataToPost = {
          tablename: val['Document Type'],
          authorized: 'U',
          actionflag: 'E',
          paymentno: val['Document No'].trim(),
          txnid: '',
          startdate: '',
          enddate: '',
          bucket: '',
          startusercreate: '',
          endusercreate: '',
        };

        var postUnapprove = await this.callApprovalApi('/DocumentApproval/approveDoc', dataToPost, 'Cancel Approval');
        console.log(postUnapprove);

        if (postUnapprove[0].Results == '0') {
          this.showApprovalResult(
            'success',
            'Document Cancelled',
            `${val['Document Type']} ${val['Document No'].trim()} cancelled.`,
          );
        } else {
          this.showApprovalResult(
            'error',
            'Cancellation Failed',
            `${val['Document Type']} ${val['Document No'].trim()} cancel failed. ${postUnapprove[0].ErrMsg}.`,
          );
        }
        this.refreshClicked();
      } catch (e) {
        console.log(e);
        this.$showAlert('error', 'Cancellation Failed', 'Unable to cancel the document.');
      }
    },

    setBucketChecked(index, checked) {
      this.$set(this.buckets[index], 'bucketChecked', checked);
      this.bucketChecked(index);
    },
    bucketChecked: function (index) {
      let bucketChecked = this.buckets[index]['bucketChecked'];
      let docs = this.displayColumns.find((column) => column.SourceIndex === index)?.Documents || this.buckets[index]['Documents'];
      if (docs.length > 0) {
        let isChecked = docs.filter((d) => d['show'] && this.canSelectDocument(d));
        isChecked.forEach((d) => (d['checked'] = bucketChecked));
      }
      this.documentCheckedCount();
      this.removeUncheckedData();
    },

    documentChecked: function (index) {
      let docs = (this.displayColumns.find((column) => column.SourceIndex === index)?.Documents || this.buckets[index]['Documents']).filter((f) => f['show'] && this.canSelectDocument(f));
      let checked = docs.filter((d) => d['checked']);
      console.log(docs);
      console.log(checked);
      this.buckets[index]['bucketChecked'] = docs.length > 0 && docs.length == checked.length;
      console.log(docs.length);
      console.log(checked.length);
      console.log(this.buckets[index]['bucketChecked']);
      this.documentCheckedCount();
      this.removeUncheckedData();
    },

    removeUncheckedData: function () {
      this.buckets.forEach((a) => {
        let unchecked = a['Documents'].filter((b) => b['checked'] == false);
        unchecked.forEach((a) => {
          this.$set(a, 'approveremarks', '');
          this.$set(a, 'documentremarks', '');
          this.$set(a, 'documents', []);
        });
      });
    },

    documentCheckedCount: function () {
      this.checkedCount = 0;
      this.selectedCheckedDocument = [];
      const seen = new Set();
      this.buckets.forEach((a) => {
        let filtered = a['Documents'].filter((b) => {
          const key = this.documentKey(b);
          if (!b.checked || !this.canSelectDocument(b) || this.isApprovedBucket(a) || seen.has(key))
            return false;
          seen.add(key);
          return true;
        });
        this.checkedCount = this.checkedCount + filtered.length;
        this.selectedCheckedDocument = this.selectedCheckedDocument.concat(filtered);
      });
    },

    infoClicked: function (e, item) {
      let parent = e.target.parentNode.parentNode.parentNode; //get the icon parent objecct
      let parentSize = parent.getBoundingClientRect(); //get the measurement of the object

      let infoLeft = parentSize.left - (this.showInfoPopupDetailsMore ? 610 : 450); //405
      let infoTop = parentSize.top;
      let infoHeight = parentSize.height + 150; //+ 150 to make it bigger

      if (infoLeft <= 45) {
        infoLeft = parentSize.right + 5;
      }
      if (parentSize.top > window.innerHeight * 0.6) {
        //if more than 60% then reduce the top so the height wont exceed the page
        infoTop = infoTop - 150;
      }

      this.$set(this.infoPopupStyle, 'left', `${infoLeft}px`);
      this.$set(this.infoPopupStyle, 'top', `${infoTop}px`);
      this.$set(this.infoPopupStyle, 'height', `${infoHeight}px`);
      // this.$set(this.infoPopupStyle, 'width', this.showInfoPopupDetailsMore ? '500px' : '400px');

      this.infoPopupDetails = item;
      this.showInfoPopup = true;
    },

    infoMoreClicked: function (e) {
      this.showInfoPopupDetailsMore = !this.showInfoPopupDetailsMore;
      // console.log(e);
      // console.log(e.target.parentNode.parentNode.getBoundingClientRect().left);
      let parent = e.target.parentNode.parentNode;
      let parentSize = parent.getBoundingClientRect();
      // console.log(this.showInfoPopupDetailsMore);
      // console.log(parentSize.left);
      let infoLeft = parentSize.left - (this.showInfoPopupDetailsMore ? 160 : -160);
      // console.log(infoLeft);
      if (infoLeft <= 45) {
        infoLeft = parentSize.right + 5;
        infoLeft = infoLeft + 160;
      }
      // console.log(infoLeft);
      this.$set(this.infoPopupStyle, 'left', `${infoLeft}px`);
    },

    matchesWorkOrderFilters: function (doc) {
      if (!this.isWorkOrderDoc(doc) || !['ALL', 'EMSJOB'].includes(this.appliedFilters?.documentType)) return true;
      const filters = this.appliedFilters.workOrderFilters;
      const vehicle = this.vehicleList.find((item) => String(item['Inventory #'] || '').trim().toLowerCase() === String(doc['Vehicle #'] || '').trim().toLowerCase());
      return (
        (!filters.vehicle.length || filters.vehicle.some((item) => String(item['Inventory #'] || '').trim().toLowerCase() === String(doc['Vehicle #'] || '').trim().toLowerCase())) &&
        (!filters.categories.length || filters.categories.some((item) => String(item['Vehicle Category']).trim().toLowerCase() === String(vehicle?.['Vehicle Category'] || '').trim().toLowerCase())) &&
        (!filters.teams.length || filters.teams.some((item) => String(item.Team).trim().toLowerCase() === String(vehicle?.Team || '').trim().toLowerCase()))
      );
    },
    filterData: function () {
      this.appliedFilters = JSON.parse(JSON.stringify(this.localFilters));
      this.activeFilterChips = this.pendingFilterChips.map((chip) => ({ ...chip }));
      const filters = this.appliedFilters;
      const limit = filters.selectedTop && filters.selectedTop.top !== 'ALL' ? Number(filters.selectedTop.top) : Infinity;
      const matchesType = (doc) => filters.documentType === 'ALL' || (this.isWorkOrderDoc(doc) ? 'EMSJOB' : doc['Document Type'] === 'Cheque' ? 'Cheque' : 'Payment') === filters.documentType;
      const matchesRequester = (doc) => {
        if (!filters.createdby) return true;
        const requester = this.isWorkOrderDoc(doc) ? doc.Requester : doc['User ID'];
        return String(requester || '').trim().toLowerCase() === filters.createdby.trim().toLowerCase();
      };
      const matchesAmount = (doc) => {
        if (!filters.amount) return true;
        const amount = parseFloat(doc['Total Amount']);
        if (filters.amountOperationIndex == 0) return amount >= Number(filters.amount);
        if (filters.amountOperationIndex == 1) return amount <= Number(filters.amount);
        if (filters.amountOperationIndex == 2) return amount === Number(filters.amount);
        return true;
      };
      this.buckets.forEach((b, bi) => {
        if (this.isApprovedBucket(b)) {
          let shown = 0;
          b['Documents'].forEach((doc) => this.$set(doc, 'show', matchesType(doc) && this.matchesWorkOrderFilters(doc) && matchesRequester(doc) && matchesAmount(doc) && shown++ < limit));
          b['FilteredCount'] = b['Documents'].filter((doc) => doc.show).length;
          return;
        }

        b['Documents'].forEach((t) => this.$set(t, 'show', false));
        let filterStatus =
          filters.selectedStatus ?
            filters.selectedStatus['Status'].trim() == 'All' ?
              ''
            : filters.selectedStatus['Status'].trim()
          : '';
        let filterIssued = filters.selectedIssuedTo.map((item) => String(item['Issued To Code'] || '').trim().toLowerCase()).filter(Boolean);
        let filterPaymentType = filters.selectedPaymentType ? filters.selectedPaymentType['Payment Type'].trim() : '';
        let chargesCode = filters.selectedChargesCode.map((s) => s['Charge Code']);
        console.log(chargesCode);
        console.log(filterStatus);
        let toShow = b['Documents'].filter((d) => {
          try {
            let result = matchesType(d) && this.matchesWorkOrderFilters(d);
            if (filterStatus) {
              if (filterStatus == 'Rejected') {
                result = result && this.isRejectedDocument(d);
                console.log(result);
              } else if (filterStatus == 'Pending') {
                result = result && !this.isRejectedDocument(d);
                console.log(result);
              }
            }

            if (filterIssued.length && !this.isWorkOrderDoc(d)) {
              result = result && filterIssued.includes(String(d['Issued To Code'] || '').trim().toLowerCase());
            }

            if (filterPaymentType && !this.isWorkOrderDoc(d)) {
              result = result && d['PaymentType'].trim().includes(filterPaymentType);
            }

            result = result && matchesRequester(d);

            if (chargesCode.length > 0 && !this.isWorkOrderDoc(d)) {
              result =
                result &&
                Array.isArray(d['AdditionalDetails']) &&
                d['AdditionalDetails'].some((e) => e['ChargesCode'] && chargesCode.includes(e['ChargesCode'].trim()));
            }

            result = result && matchesAmount(d);

            if (result) {
              return d;
            } else {
              d['checked'] = false;
            }
          } catch (error) {
            console.log(error);
            console.log(d);
          }
        });

        toShow = toShow.slice(0, limit);
        b['Documents'].forEach((doc) => { if (!toShow.includes(doc)) doc.checked = false; });
        b['FilteredCount'] = toShow.length;
        console.log(b['FilteredCount']);
        console.log(b);

        const selectable = toShow.filter((doc) => this.canSelectDocument(doc));
        b['bucketChecked'] = selectable.length > 0 && selectable.every((doc) => doc.checked);
        this.documentCheckedCount();
        toShow.forEach((t) => this.$set(t, 'show', true));
      });
    },

    approveButtonClick: function () {
      if (this.approvalSetupMissing) return;
      if (this.checkedCount > 0) {
        this.popupEntryRequest = 'Approve';
        this.resetDecisionPopup();
        this.showApprovePopup = true;
      } else {
        this.$showAlert('warning', 'No Documents Selected', 'Please select a document before making an approval.');
      }
    },

    rejectButtonClick: function () {
      if (this.approvalSetupMissing) return;
      if (this.checkedCount > 0) {
        this.popupEntryRequest = 'Reject';
        this.resetDecisionPopup();
        this.showApprovePopup = true;
      } else {
        this.$showAlert('warning', 'No Documents Selected', 'Please select a document before making a rejection.');
      }
    },

    documentSelected: async function (att, index) {
      const doc = this.selectedCheckedDocument[index];
      var attList = [];
      for (var i = 0; i < att.length; i++) {
        var base64 = await this.$convertFile.toBase64(att[i]);
        attList.push({
          docatt: att[i].name,
          filetype: att[i].type,
          attfile: base64,
          docaction: 'N',
        });
      }
      this.$set(doc, 'documents', attList);
    },

    approveClick: async function () {
      if (this.approvalSetupMissing || this.loading || !this.selectedCheckedDocument.length) return;
      this.decisionRequiredErrors = [];
      this.Emessages = [];
      if (this.popupEntryRequest === 'Reject') {
        this.selectedCheckedDocument.forEach((doc, index) => {
          const remark = this.useSharedRemark ? this.sharedDecisionRemark : doc.approveremarks;
          if (!String(remark || '').trim())
            this.decisionRequiredErrors.push({ Key: index, Value: doc['Document No'] || doc.TxnID });
        });
      }
      if (this.decisionRequiredErrors.length) return;
      await this.quickApprovedClicked(this.popupEntryRequest === 'Reject' ? 'Rejected' : 'Approved');
      if (this.selectedCheckedDocument.length) return;
      this.showApprovePopup = false;
      this.popupEntryRequest = '';
    },

    cancelClick: function () {
      if (!this.loading) this.showApprovePopup = false;
    },

    showAttachment: async function (e, item) {
      this.loading = true;
      this.loadText = 'Getting Attachments';
      this.attachmentList = await this.callApprovalApi('/DocumentApproval/GetJobDocuments?jobno=' + item['Document No']);
      var tempIssuedTo = item['Issued To'];
      var tempBLNo = item['BL No'] ? item['BL No'] : '';
      var tempCurrency = this.documentCurrency(item);
      var tempTotalAmount = item['Total Amount'];
      this.attAdditionalDetails = item['AdditionalDetails'];
      this.attachmentList.forEach((a) => {
        a['Issued To'] = tempIssuedTo;
        a['BL No'] = tempBLNo;
        a['Currency'] = tempCurrency;
        a['Total Amount'] = tempTotalAmount;
      });
      // this.attachmentlist['Issued To1'] = item[0]['Issued To'];
      // this.attachmentList['BL No1'] = item[0]['BL No'];
      if (this.attachmentList.length < 1) {
        //no attachment records... do right side pop up notification to show message.
      } else {
        this.convertImage(); //(this.attachmentList);
        this.attToView = this.attachmentList[0];
      }

      this.loading = false;
      this.showAttachmentPopup = true;
    },

    navAtt: function (clicked) {
      var currentindex = this.attachmentList.indexOf(this.attToView);
      var length = this.attachmentList.length - 1;

      if (clicked == 'prev') {
        if (currentindex == 0) currentindex = length;
        else currentindex = currentindex - 1;
      } else {
        if (currentindex == length) currentindex = 0;
        else currentindex = currentindex + 1;
      }

      this.attToView = this.attachmentList[currentindex];
    },

    closeshowAttachmentPopup: function () {
      this.showAttachmentPopup = false;
      if (this.returnToWorkOrderReview) {
        this.returnToWorkOrderReview = false;
      }
    },

    convertImage: function () {
      //loop to get and convert all attImg
      try {
        this.attachmentList.forEach((j) => {
          var type = '';
          if (j['AttImageType'] == null) {
            j['AttImageType'] = 'n/a';
          } else {
            j['AttImageType'] = j['AttImageType'].trim();
            type = j['AttImageType'];
          }

          //if empty
          if (j['AttImage'] == null || j['AttImage'].length == 0) {
            j['AttImage'] = 'https://i.ibb.co/kyZfpjx/no-image.png';
            //if value = pdf file
          } else if (type === '.pdf') {
            j['AttImage'] = 'data:application/pdf;base64,' + j['AttImage'];
          } else if (type === '.xlsx') {
            j['AttImage'] =
              'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
              j['AttImage'].slice(0, -1);
          } else if (type === '.ogg') {
            j['AttImage'] = 'data:audio/ogg;base64,' + j['AttImage'];
          } else if (type === '.pptx') {
            j['AttImage'] =
              'data:application/vnd.openxmlformats-officedocument.presentationml.presentation;base64,' +
              j['AttImage'].substring(0, j['AttImage'].length - 4);
          } else if (type === '.docx') {
            j['AttImage'] =
              'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,' + j['AttImage'];
          } else if (type === '.msg') {
            j['AttImage'] = 'data:application/vnd.msoutlook;base64,' + j['AttImage'];
          } else if (
            type === '.jpeg' ||
            type === '.jpg' ||
            type === 'image/Jpeg' ||
            type === 'image/png' ||
            type === '.jpeg' ||
            type === 'image/jpeg' ||
            type === '.png' ||
            type === '.gif'
          ) {
            j['AttImage'] = 'data:image/png;base64,' + j['AttImage'];
          } else {
            j['AttImage'] = 'data:application/octet-stream;base64,' + j['AttImage'];
          }
        });
      } catch (e) {
        console.log(e);
      }
    },

    windowResize: function () {
      var width = window.innerWidth;
    },

    downloadpop: function () {
      var doc = this.attToView['AttImage'];
      var status = this.attToView['Status'].trim();

      var type = this.attToView['AttImageType'].trim();

      var link = document.createElement('a');
      link.href = doc;
      link.download = this.attToView['Job No'].trim() + '_' + status + type;
      link.click();
    },

    generateButtonClick: function () {
      if (this.approvalSetupMissing) return;
      this.bankcode = [];
      this.fileno = [];
      this.fileNoTableData = [];
      this.selectedBankCode = null;
      this.selectedFileNo = null;
      console.log('here1');
      this.showGenerateListPopup = !this.showGenerateListPopup;
      this.loading = true;
      this.loadText = 'Getting Data';
      console.log('here2');
      this.getGenerateListData();
      console.log('here3');
      this.loading = false;
    },

    generateListButtonClick: async function (e) {
      // this.bankcode = [];
      // this.fileno = [];
      // this.fileNoTableData = [];
      // this.selectedBankCode = null;
      // this.selectedFileNo = null;
      console.log(e);
      if (e == 'Cancel') {
        console.log('can');
        this.showGenerateListPopup = false;
      } else if (e == 'Generate New List') {
        console.log('genquestion');
        if (!this.selectedBankCode) {
          console.log('quest2');
          // this.$alert.showAlert('Invalid Date Field', 'Please select a valid Date Field from the dropdown list', 'error');
          this.$showAlert(
            'error',
            'No Bank Selected',
            'Bank cannot be empty. Please select a bank from the Bank List.',
          );
        } else {
          console.log('new file');
          // this.showGenerateListPopupLoading = true;
          // this.showGenerateListPopupLoadingText = 'Generating List';
          this.$fullscreenLoading('Generating List');
          await this.getGenerateListTableData('NEWFILENO');
          // this.showGenerateListPopupLoading = false;
          // this.showGenerateListPopupLoadingText = '';
          this.$fullscreenLoading();
        }
      } else if (e == 'Print List') {
        console.log('print can');
        if (!this.selectedFileNo) {
          console.log('print cannot');
          this.$showAlert('error', 'No File Selected', 'File cannot be empty. Please select a file before printing.');
        } else {
          console.log('can print');
          console.log(this.fileNoTableData);
          var data = {
            fileno: this.selectedFileNo['FileNo'],
            dateprint: new Date().formatString('yyyy-MM-dd HH:mm'),
            printby: this.userid,
            document: this.fileNoTableData,
          };
          console.log(data);
          this.designerV2Data = data;
          this.showDesignerV2 = true;
        }
      }
    },

    getGenerateListData: async function () {
      this.bankCode = await this.callApprovalApi('/DocumentApproval/getListMaster?masterfile=bank&module=da');
      this.bankCode.forEach((b) => {
        b['BankCodeBankName'] = `${b['BankCode']}-${b['BankName']}`;
      });
      console.log(this.bankCode);
    },

    getGenerateListTableData: async function (action) {
      console.log('hereqq');
      console.log(action);
      let tempfileno = '';
      let tempaction = '';
      if (action.trim() == 'GETALLFILENO') {
        tempfileno = '';
        tempaction = '';
        let generateListJSON = {
          date: '',
          bankcode: this.selectedBankCode['BankCode'],
          action: tempaction,
          fileno: tempfileno,
        };
        this.fileno = [];
        this.fileno = await this.callApprovalApi('/DocumentApproval/DocApprovalGenerateList', generateListJSON);
      } else if (action.trim() == 'GETFILENODATA') {
        tempfileno = this.selectedFileNo['FileNo'];
        tempaction = '';
        let generateListJSON = {
          date: '',
          bankcode: this.selectedBankCode['BankCode'],
          action: tempaction,
          fileno: tempfileno,
        };
        this.fileNoTableData = await this.callApprovalApi('/DocumentApproval/DocApprovalGenerateList', generateListJSON);
      } else if (action.trim() == 'NEWFILENO') {
        tempfileno = '';
        tempaction = 'N';
        let generateListJSON = {
          date: '',
          bankcode: this.selectedBankCode['BankCode'],
          action: tempaction,
          fileno: tempfileno,
        };
        this.fileno = await this.callApprovalApi('/DocumentApproval/DocApprovalGenerateList', generateListJSON, 'Generate List');
        let Results = this.fileno[0]['Results'];
        let ErrMsg = this.fileno[0]['ErrMsg'];
        let FileNo = this.fileno[0]['FileNo'];

        if (Results == 1) {
          await this.getGenerateListTableData('GETALLFILENO');
          this.selectedFileNo = null;
          this.$showAlert('info', 'Info', ErrMsg.trim());
        } else if (Results == 0) {
          this.newFileNo = FileNo.trim();
          this.fileno = [];
          console.log(this.newFileNo);
          await this.getGenerateListTableData('GETALLFILENO');
          console.log(this.fileno);
          let tempselectedfileno = this.fileno.find((b) => b['FileNo'] == this.newFileNo);
          console.log(tempselectedfileno);
          var newIndex = this.fileno.indexOf(tempselectedfileno);
          console.log(newIndex);
          this.selectedFileNo = this.fileno[newIndex];
          console.log(this.selectedFileNo);
          this.newFileNo = null;
        }
      }
    },

    backendError: async function () {
      // let data = await this.callApprovalApi('/UserManagement/GetListMasterError');
      // this.$store.state['errorDetails'] = { name: 'Azizi' };
      // this.$bus.$emit('pageError', 'hey');
    },

    apiError: async function () {
      // let data = await this.callApprovalApi('/UserManagement/GetListMasterError'); //qyvoon temp remove
    },

    selectVisibleDocOnly: function (e) {
      // e is the index of the bucket
      let selectAllVisible = this.bucketsSelectVisibleTooltip[e]['text'].split(' ')[0] == 'Select';
      let { docIndexes, bucketIndex } = this.getVisibleDocs(e);

      if (!bucketIndex.length) return;
      docIndexes.forEach((i, position) => {
        const doc = this.buckets[bucketIndex[position]]['Documents'][i];
        if (this.canSelectDocument(doc)) doc['checked'] = selectAllVisible;
      });
      this.documentChecked(e);

      this.$set(
        this.bucketsSelectVisibleTooltip[e],
        'text',
        `${selectAllVisible ? 'Unselect' : 'Select'} Visible Document(s) Only`,
      );
    },
    getVisibleDocs: function (e) {
      let bucket = document.querySelector('.bucket[data-source-index="' + e + '"]');
      if (!bucket) return { docIndexes: [], bucketIndex: [] };
      let content = bucket.getElementsByClassName('content')[0];
      let contentRect = content.getBoundingClientRect();

      let top = contentRect.top - 5;
      let bottom = contentRect.bottom + 5;

      let allItems = Array.from(content.getElementsByClassName('content-item'));
      let visibleItems = allItems.filter((a) => {
        let itemRect = a.getBoundingClientRect();
        let itemTop = itemRect.top;
        let itemBottom = itemRect.bottom;

        // return itemTop >= top && itemBottom <= bottom; // Only fully visible item is selected
        // return (itemTop >= top && itemTop <= bottom) || (itemBottom >= top && itemBottom <= bottom); // Item partially visible is selected
        return itemRect.height > 0 && itemTop + 5 >= top && itemTop + itemRect.height / 2 <= bottom; // Item partially visible is selected but the top must be visible
      });

      let docIndexes = visibleItems.map((v) => Number(v.getAttribute('docIndex')));
      let bucketIndex = visibleItems.map((v) => Number(v.getAttribute('bucketIndex')));

      return { docIndexes: docIndexes, bucketIndex: bucketIndex };
    },
    bucketScrolling: function (ind) {
      let { docIndexes, bucketIndex } = this.getVisibleDocs(ind);
      if (!bucketIndex.length || !this.bucketsSelectVisibleTooltip[ind]) return;

      let checkCount = 0;
      docIndexes.forEach((i, position) => {
        if (this.buckets[bucketIndex[position]]['Documents'][i]['checked']) {
          checkCount++;
        }
      });

      this.$set(
        this.bucketsSelectVisibleTooltip[ind],
        'text',
        `${checkCount == docIndexes.length ? 'Unselect' : 'Select'} Visible Document(s) Only`,
      );
    },

    windowResize: function () {
      this.narrowWindow = window.innerWidth < 900;
    },
  },
  async mounted() {
    this.selectedDocType = this.docType[0];
    this.selectedTop = this.topList[this.topList.length - 2]; //get the value 500 object
    this.selectedStatus = this.statusType[0];

    this.loading = true;
    this.loadText = 'Getting Details';

    try {
      const userDetails = await this.callApprovalApi('/Login/GetDetails');
      this.userid = userDetails.LoginUserId;
      this.approvalPreferenceKey = 'document-approval:' + userDetails.CoCode + ':' + this.userid + ':rejected-column';
      try {
        this.showRejectedColumn = localStorage.getItem(this.approvalPreferenceKey) === 'true';
      } catch (error) {
        /* Storage may be unavailable. */
      }
    } finally {
      this.loading = false;
    }
    this.refreshingCount++;
    try {
      const results = await Promise.allSettled([
        this.loadCompanyCurrency(),
        this.callApprovalApi('/Vehicle/GetVehicleSelectorList', {}).then((items) => {
          this.vehicleList = (Array.isArray(items) ? items : []).map((item) => ({ ...item, label: `${item['Inventory #']} - ${item['Inventory Code']}` }));
        }),
        this.callApprovalApi('/EquipmentSchedule/GetVehicleCategory').then((items) => {
          this.vehicleCategoryList = Array.isArray(items) ? items : [];
        }),
        this.callApprovalApi(`/DocumentApproval/GetIssuedTo?tablename=all&userid=${this.userid}`).then((items) => {
          this.issuedTo = items;
        }),
        this.callApprovalApi('/DocumentApproval/getListMaster?masterfile=paymenttype').then((items) => {
          this.paymentType = items;
        }),
        this.callApprovalApi('/DocumentApproval/getListMaster?masterfile=chargescode').then((items) => {
          this.chargesCode = items;
        }),
        (async () => {
          const [buckets] = await Promise.all([
            this.callApprovalApi('/DocumentApproval/getListMaster?masterfile=dashboard_documentapproval'),
            this.loadWorkOrderApprovalSettings(),
            this.loadAmountPreference(),
          ]);
          this.buckets =
            Array.isArray(buckets) ?
              buckets.map((bucket) => ({
                ...bucket,
                Documents: Array.isArray(bucket.Documents) ? bucket.Documents : [],
              }))
            : [];
          this.bucketsSelectVisibleTooltip = this.buckets.map((bucket, index) => ({
            bucket: `Bucket${index}`,
            text: 'Select Visible Document(s) Only',
          }));
          await this.callAPI();
          this.filterData();
          this.documentCheckedCount();
        })(),
      ]);
      const errors = results
        .filter((result) => result.status === 'rejected')
        .map((result) => result.reason?.message || 'Unable to load approval data.');
      if (errors.length) this.refreshError = [this.refreshError, ...errors].filter(Boolean).join(' ');
    } finally {
      this.refreshingCount--;
      this.initialDataLoading = false;
    }
    if (window.innerWidth < 900) {
      this.narrowWindow = true;
    }
    window.addEventListener('resize', this.windowResize);
  },
  watch: {
    localFilters: {
      deep: true,
      handler() {
        this.filterData();
        this.documentCheckedCount();
      },
    },
    amountPreference: {
      deep: true,
      handler() {
        clearTimeout(this.amountPreferenceTimer);
        if (!this.amountPreferenceLoaded) return;
        this.amountPreferenceTimer = setTimeout(() => this.saveAmountPreference(), 500);
      },
    },
    workOrderReviewSections: {
      immediate: true,
      async handler(sections) {
        const version = ++this.reviewTableRenderVersion;
        this.reviewTableData = {};
        await this.$nextTick();
        if (version !== this.reviewTableRenderVersion || this._isDestroyed) return;
        this.reviewTableData = Object.fromEntries(sections.map((section) => [section.title, section.tableRows]));
      },
    },
    showRejectedColumn(value) {
      try {
        if (this.approvalPreferenceKey) localStorage.setItem(this.approvalPreferenceKey, String(value));
      } catch (error) {
        /* Storage may be unavailable. */
      }
    },
    showApprovePopup: function (val) {
      this.Emessages = [];
      this.successMessages = [];
    },
    selectedBankCode: async function (val) {
      console.log(val);
      if (val) {
        console.log('herebank');
        this.selectedFileNo = null;
        this.generateListTableTitle = '';
        this.fileNoTableData = [];
        this.$fullscreenLoading(`Getting ${val['BankCode']} files`);
        await this.getGenerateListTableData('GETALLFILENO');
        this.$fullscreenLoading();
        console.log(this.fileno);
      }
    },
    selectedFileNo: function (val) {
      console.log(val);
      this.fileNoTableData = [];
      this.generateListTableTitle = '';
      // console.log(this.generateListTableTitle);
      if (val) {
        console.log('herefile');
        this.getGenerateListTableData('GETFILENODATA');
        this.generateListTableTitle = val['FileNo'];
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.amountPreferenceTimer);
    window.removeEventListener('resize', this.windowResize);
  },
  // created() {
  //   window.addEventListener('resize', this.windowResize);
  // }
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.doc-page-main {
  font-size: 12px;
  padding: 10px 24px 10px 48px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 5px;
  height: 100%;
  min-height: 0;
  > .main-column {
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  > .left-container {
    border-radius: 5px;
    max-width: 250px;
    min-width: 250px;
    width: 100%;
    background-color: white;
    height: 100%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 12;
    display: flex;
    flex-direction: column;

    > .left-container-header {
      width: 100%;
      padding: 4px 4px 4px 8px;
      position: sticky;
      top: 0;
      z-index: 3;
      flex-shrink: 0;
      background: white;
      border-bottom: 1px solid #ddd;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 4px;

      > .left-container-title {
        flex: 1;
        min-width: 0;
        font-weight: bold;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      > .collapse-filters-button {
        // Sits on the inner edge of the panel (where it meets the list), so « points the way the panel collapses
        flex: 0 0 auto;
        width: 30px;
        height: 30px;
        margin-left: auto;
        padding: 2px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 0;
        border-radius: 3px;
        background: transparent;
        box-shadow: none;
        cursor: pointer;

        &:hover:enabled {
          background-color: #eef1ff;
        }

        &:focus-visible {
          outline: 2px solid #24588e;
          outline-offset: -2px;
        }

        > img {
          width: 26px;
          height: 26px;
          transition: 0.3s;
        }
      }
    }

    > .left-container-content {
      flex: 1;
      padding: 0 5px;
      display: flex;
      flex-direction: column;

      .left-container-content-det {
        margin-top: 5px;
      }

      .doc-type-tabs {
        display: flex;
        flex-direction: column;
        gap: 1px;
        margin-top: 5px;
        background: lightgray;
        border: 1px solid lightgray;
        border-radius: 0.25rem;
        overflow: hidden;
        > button {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          margin: 0;
          padding: 0.25rem 0.5rem;
          border: 0 !important;
          border-radius: 0;
          background-color: #fafafa;
          color: #405064;
          text-align: left;
          transition: none;
          box-shadow: none;
          outline: 0 !important;
          &:hover:not(.active):not(:disabled) {
            background-color: #eef1ff;
          }
          &:focus-visible {
            outline: 2px solid #1f6fb2 !important;
            outline-offset: -2px;
          }
          &.active {
            background-color: #b3c4ff;
            color: #25345f;
          }
          > .doc-type-count {
            min-width: 20px;
            padding: 0 6px;
            border-radius: 9px;
            background-color: white;
            border: 1px solid lightgray;
            font-size: 10px;
            text-align: center;
          }
        }
      }

      .section-title {
        margin-top: 10px;
        font-weight: bold;
      }

      .filter-fields {
        display: flex;
        flex-direction: column;
        margin-top: 5px;
      }

      .payment-filters-fields {
        display: flex;
        flex-direction: column;
      }

      .amount-container {
        // Operator toggle sits to the left of the Amount input (side by side, never stacked).
        display: flex;
        align-items: flex-end;
        gap: 4px;
        > .amount-operators {
          flex: 0 0 auto;
          display: inline-flex;
          gap: 1px;
          margin-bottom: 9px;
          background: lightgray;
          border: 1px solid lightgray;
          border-radius: 0.25rem;
          > button {
            margin: 0;
            padding: 0 7px;
            line-height: 18px;
            border: 0 !important;
            border-radius: 0;
            background-color: #fafafa;
            color: #405064;
            transition: none;
            box-shadow: none;
            outline: 0 !important;
            &:first-child {
              border-radius: calc(0.25rem - 1px) 0 0 calc(0.25rem - 1px);
            }
            &:last-child {
              border-radius: 0 calc(0.25rem - 1px) calc(0.25rem - 1px) 0;
            }
            &:hover:not(.active):not(:disabled) {
              background-color: #eef1ff;
            }
            &:focus-visible {
              outline: 2px solid #1f6fb2 !important;
              outline-offset: -2px;
            }
            &.active {
              background-color: #b3c4ff;
              color: #25345f;
            }
          }
        }
        > :last-child {
          flex: 1;
          min-width: 0;
        }
      }

      > .left-container-content-footer {
        margin-top: auto;
        position: sticky;
        bottom: 0;
        z-index: 2;
        padding: 8px 0 4px;
        background: white;
        border-top: 1px solid #ddd;
      }

      .left-container-content-button {
        width: 100%;
        position: sticky;
        bottom: 0;
        background: white;
        display: flex;
        flex-direction: row;

        button {
          margin: 0 5px;
          width: auto;
          flex: 1 1 0;
          min-width: 0;
          &:first-child {
            margin-left: 0;
          }
          &:last-child {
            margin-right: 0;
          }
        }

        .reset-filter-button {
          // Secondary action: neutral grey and about half the width of Refresh
          flex: 1 1 0;
          max-width: none;
          background-color: #e4e6eb;
          color: #333;

          &:hover:enabled {
            background-color: #eceef2;
          }
        }

        .refresh-button {
          // Same look as the old header Refresh button (global button colour), just moved down here
          flex: 2 1 0;
          min-width: 100px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
      }
    }
  }

  > .left-tab-close {
    max-width: 40px;
    min-width: 40px;
    overflow: hidden;
    > .left-container-header {
      flex-direction: column;
      border-bottom: none;
      justify-content: flex-start;
      align-items: center;
      gap: 2px;
      padding: 4px 5px 5px;
      > .collapse-filters-button {
        margin-left: 0; // centred in the narrow rail
      }
    }
  }

  .collapsed-refresh-button {
    width: 30px;
    height: 34px;
    padding: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border: 0 !important;
    transition: none;
    &,
    &:hover,
    &:active {
      background: transparent !important;
      box-shadow: none !important;
      transform: none !important;
      filter: none !important;
    }
    &:focus-visible {
      outline: 2px solid #24588e;
      outline-offset: -2px;
    }
  }

  .doc-type-tabs > button:disabled,
  .amount-operators > button:disabled {
    opacity: 0.5;
    background-color: #fafafa !important;
    color: #405064 !important;
    &.active {
      background-color: #b3c4ff !important;
      color: #25345f !important;
    }
  }

  .list-refresh-indicator {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 24px;
    background-color: white;
    border-radius: 5px;
    color: #000;
    font-size: 14px;
  }

  > .main-column > .action-bar {
    background-color: white;
    border-radius: 5px;
    padding: 4px 8px;
    margin-bottom: 5px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    > .selected-count {
      padding: 3px 6px;
      border: 1px solid rgba(150, 150, 150, 0.3);
      border-radius: 5px;
      white-space: nowrap;
      > b {
        margin-left: 4px;
      }
    }
    > button {
      width: auto;
      min-width: 110px;
      flex: 0 0 auto;
    }
    > .api-messages-button {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      min-width: 0;
      padding: 4px 6px;
      background: transparent;
      > img { width: 24px; height: 24px; }
      > span { padding: 1px 4px; border-radius: 3px; font-size: 11px; }
    }
    > .action-separator {
      align-self: stretch;
      flex: 0 0 1px;
      width: 1px;
      margin: 3px 6px;
      background-color: rgba(150, 150, 150, 0.45);
    }
    > .action-display-options {
      margin-left: auto;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 4px;
    }
    .last-refreshed {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: #667085;
      font-size: 11px;
      line-height: 1.1;
      white-space: nowrap;
      > time {
        font-variant-numeric: tabular-nums;
      }
    }
    > .filters-summary {
      display: flex;
      flex: 1 0 100%;
      flex-wrap: wrap;
      min-width: 0;
      margin-left: 0;
      gap: 8px;
      > .filters-text {
        display: flex;
        flex: 1;
        flex-wrap: wrap;
        justify-content: flex-start;
        min-width: 0;
        gap: 4px;
      }
    }
    > .filters-summary > .filters-text {
      > .filter-chip {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        max-width: min(200px, 100%);
        padding: 2px 6px;
        border: 1px solid rgba(150, 150, 150, 0.3);
        border-radius: 10px;
        background-color: #f7f7f7;
        font-size: 10px;
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        > .caption {
          font-weight: bold;
          margin-right: 3px;
          flex: 0 0 auto;
        }
        > .value {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        > .chip-remove {
          flex: 0 0 auto;
          width: auto;
          min-width: 0;
          margin: 0 0 0 4px;
          padding: 0 2px;
          border: 0 !important;
          background: transparent;
          box-shadow: none;
          font-size: 11px;
          line-height: 1;
          color: #666;
          cursor: pointer;
          &:hover {
            color: #c0392b;
          }
          &:focus-visible {
            outline: 2px solid #1f6fb2 !important;
          }
        }
      }
      > .clear-all-filters {
        flex: 0 0 auto;
        width: auto;
        min-width: 0;
        margin: 0;
        padding: 0 4px;
        border: 0 !important;
        background: transparent;
        box-shadow: none;
        font-size: 10px;
        color: #1f6fb2;
        white-space: nowrap;
        cursor: pointer;
        &:hover {
          text-decoration: underline;
        }
      }
      > .no-active-filters {
        font-size: 10px;
        color: #666;
        white-space: nowrap;
      }
    }
  }
  > .main-column > .bottom-container {
    background-color: white;
    border-radius: 5px;
    padding: 5px;
    flex: 1;
    min-height: 0;
    display: flex;
    gap: 5px;
    box-sizing: border-box;
    width: 100%;
    overflow-x: auto;

    > .bucket {
      height: 100%;
      min-width: 220px;
      flex: 1 0 0;
      box-sizing: border-box;
      border: 1px solid black;
      display: flex;
      flex-direction: column;

      > .header {
        height: 72px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        padding: 6px;
        position: relative;

        > .title {
          flex: 1;
          min-height: 0;
          padding: 0 4px;
          box-sizing: border-box;
          width: 100%;
          text-align: center;
          font-size: 1.2em;
          font-weight: bold;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        > .count {
          width: 100%;
          text-align: center;
          border-bottom: 1px solid gray;
          padding: 0 24px 4px;
          box-sizing: border-box;
          font-size: 11px;
        }

        > .tooltip-button {
          cursor: pointer;
          position: absolute;
          bottom: 6px;
          right: 6px;
          z-index: 9;
        }
      }

      > .content {
        height: 100%;
        overflow: auto;
        padding: 5px;

        > .content-item {
          border: 1px solid gray;
          padding: 5px;
          margin-bottom: 5px;

          > .long-text {
            width: 100%;
            > div {
              display: flex;
              align-items: flex-start;
              gap: 6px;
              > .long-text-label {
                flex: 0 0 68px;
                white-space: nowrap;
              }
              > .long-text-value {
                flex: 1;
                min-width: 0;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: normal;
                overflow-wrap: anywhere;
              }
            }
          }

          > .content-row {
            width: 100%;
            display: flex;
            align-items: center;

            > .doc-no {
              font-weight: bold;
              font-size: 1.1em;
            }

            > .doc-tag {
              min-width: 90px;
              padding: 1px 6px;
              word-spacing: -1px;
              text-align: center;
              white-space: nowrap;
              color: black;
              &.doc-tag--payment {
                background-color: rgb(0, 176, 240);
              }
              &.doc-tag--cheque {
                background-color: rgb(180, 150, 230);
              }
              &.doc-tag--workorder {
                background-color: rgb(255, 192, 0);
              }
            }

            > .amount {
              font-weight: bold;
            }

            > .icon-button > img {
              height: 20px;
              margin-left: 3px;
              cursor: pointer;
            }

            > :nth-child(1) {
              width: 100%;
            }

            > :nth-child(2) {
              width: fit-content;
              white-space: nowrap;
            }
          }

          > :first-child {
            margin-bottom: 10px;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }
      }

      &:last-child {
        margin-right: 0;
      }
    }
  }

  > .info-popup {
    position: fixed;
    z-index: 999;
    background-color: white;
    border-radius: 5px;
    box-shadow: 0 0 8px gray;
    // width: 500px;
    height: 200px;
    overflow-y: scroll;

    > .info-header {
      display: flex;
      align-items: center;
      padding: 3px 5px;
      // border-bottom: 1px solid gray;

      > :first-child {
        width: 100%;
        font-weight: bold;
      }

      > :nth-child(2) {
        min-width: 45px;
        max-width: 45px;
        cursor: pointer;
      }

      > :last-child {
        cursor: pointer;
      }
    }

    > .info-content {
      overflow: auto;

      // > .info-content-header {
      //   display: flex;
      // }

      > .info-content-table {
        border-collapse: collapse;
        width: 100%;
        // border: 1px solid black;
        // border-bottom: 1px solid gray;
        table-layout: auto;

        > tr > th,
        td {
          padding: 3px;
          border: 1px solid gray;

          &:nth-child(1) {
            min-width: 25px;
            max-width: 30px;
          }
          &:nth-child(2) {
            min-width: 215px;
            max-width: 215px;
          }
          &:nth-child(3) {
            min-width: 30px;
            max-width: 30px;
          }
          &:nth-child(4) {
            min-width: 80px;
            max-width: 80px;
            text-align: right;
          }
          &:nth-child(5) {
            min-width: 80px;
            max-width: 80px;
            text-align: right;
          }
          &:nth-child(6) {
            min-width: 80px;
            max-width: 80px;
            text-align: right;
          }
          &:last-child {
            min-width: 90px;
            max-width: 90px;
            text-align: left;
          }
        }

        .tableTooltip .tableTooltipText {
          visibility: hidden;
          position: absolute;
          z-index: 10;
          background-color: black;
          color: #fff;
          text-align: center;
          border-radius: 5px;
          padding: 2px;
        }

        .tableTooltip:hover .tableTooltipText {
          visibility: visible;
        }
        // th, td {
        //   border: 1px solid gray;
        //   // padding: 5px;
        // }
      }

      // > .info-content-row, .info-content-header {
      //   display: flex;
      //   align-items: center;
      //   padding: 1px 5px;
      //   border-bottom: 1px solid rgba(150,150,150,0.3);
      //   font-size: 0.95em;

      //   > :not(:first-child) {
      //     border-left: 1px solid black;
      //   }

      //   > * {
      //     padding: 3px;
      //   }

      //   > :first-child { min-width: 25px; max-width: 30px; }
      //   > :nth-child(2) { min-width: 215px; max-width: 215px; }
      //   > :nth-child(3) { min-width: 25px; max-width: 30px; }
      //   > :nth-child(4) { min-width: 45px; max-width: 45px; }
      //   > :nth-child(5) { min-width: 45px; max-width: 45px; }
      //   > :nth-child(6) { min-width: 45px; max-width: 45px; }
      //   > :last-child { min-width: 90px; max-width: 90px; margin-left: 5px; }
      // }
    }
  }

  .attachments-projector-container {
    position: fixed;
    top: $top-nav-height;
    left: 0;
    width: 100%;
    height: calc(100% - #{$top-nav-height});
    background: rgba(0, 0, 0, 0.8);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
    &.workorder-attachment-preview { z-index: 100000; }
    > .close-button {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 50px;
      height: 50px;
      padding: 0;
      border: none;
      border-radius: 50%;
      background: white;
      color: black;
      font-size: 32px;
      line-height: 1;
      cursor: pointer;
    }
  }
  .attachments-projector {
    width: 90%;
    height: 90%;
    display: flex;
    text-align: left;
  }
  .attachment-projection-container {
    flex: 3;
    min-width: 0;
    display: flex;
    align-items: center;
    background: black;
  }
  .attachment-projection {
    flex: 1;
    min-width: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    > img,
    > object {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    > audio {
      width: 90%;
    }
  }
  .previous-attachment,
  .next-attachment {
    flex: 0 0 36px;
    > button {
      width: 36px;
      height: 75px;
      padding: 0;
      border: none;
      background: transparent;
      color: white;
      font-size: 36px;
      cursor: pointer;
      &:hover {
        background: rgba(200, 200, 200, 0.8);
      }
    }
  }
  .attachment-preview-unavailable {
    padding: 24px;
    text-align: center;
    color: white;
    > span {
      display: block;
      margin-bottom: 16px;
    }
  }
  .attachment-details {
    flex: 1;
    min-width: 260px;
    padding: 12px;
    background: white;
    color: black;
    overflow: auto;
    .count {
      margin-bottom: 12px;
      font-weight: bold;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 12px 0;
      th,
      td {
        padding: 5px;
        border: 1px solid #ccc;
        vertical-align: top;
        overflow-wrap: anywhere;
      }
      th {
        width: 40%;
      }
    }
  }
  .attachment-empty {
    height: auto;
    color: white;
  }

  .attachmentAddInfoMain {
    width: auto;
    height: 160px;
    // padding: 5px;
    overflow-y: scroll;
  }

  .addInfoRow {
    width: 100%;
    height: auto;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    border-bottom: solid 1px #0001;

    > div {
      padding: 0 4px;
    }
  }

  .generateList-main {
    height: 100%;
    text-align: left;

    > .generateList-filter {
      margin: 5px;
      height: 15%;
    }

    > .generateList-table {
      margin: 5px;
      height: 80%;
    }
  }

  > .designerv2-popup {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    padding: 10px 45px;
    background-color: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999;

    > :first-child {
      background-color: white;
    }
  }
}

@media only screen and (max-width: 900px) {
  .doc-page-main {
    padding: 10px;
    flex-direction: column;
    > .left-container {
      max-width: none;
      min-width: 0;
      height: auto;
      max-height: 45%;
      flex: 0 0 auto;
    }
    > .left-tab-close {
      max-height: 45px;
      > .left-container-header {
        flex-direction: row;
        justify-content: flex-end;
        gap: 4px;
        padding: 4px 8px;
        > .collapse-filters-button {
          margin-left: 0;
        }
      }
    }
    > .main-column > .action-bar {
      flex-wrap: wrap;
      > button {
        flex: 1;
        min-width: 0;
      }
      > .filters-summary {
        flex-basis: 100%;
        margin-left: 0;
      }
    }
    > .main-column > .bottom-container {
      overflow-x: auto;
    }
  }
}

.refresh-error {
  padding: 8px 12px;
  color: #8a4b10;
  background: #fff6e8;
  font-size: 12px;
}
.review-link {
  border: 0;
  padding: 3px 4px;
  background: transparent;
  color: #24588e;
  text-decoration: underline;
  cursor: pointer;
  outline: none !important;
  background: none !important;
  box-shadow: none !important ;
}

.workorder-decision-summary {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
}
.workorder-review-popup {
  padding-top: 50px !important;
}
.workorder-review-popup ::v-deep .content > .dragger {
  display: none;
}
.workorder-review-popup ::v-deep .content > .header {
  margin-bottom: 4px;
  padding-bottom: 8px;
  border-bottom: 1px solid #bdbdbd;
}
.workorder-review-popup ::v-deep .content > .header > .title {
  margin: 0;
}
.workorder-review-popup ::v-deep .content > .header,
.workorder-review-popup ::v-deep .content > .review-decision-footer {
  flex-shrink: 0;
}
.workorder-review-popup ::v-deep .content > .review-decision-footer {
  margin-top: 0;
}
.workorder-review {
  flex: 1;
  min-height: 0;
  padding: 0 8px 12px;
  color: black;
  font-size: 12px;
  text-align: left;
}
.review-job-details {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px 10px;
  align-items: center;
}
.review-job-details > .review-vehicle,
.review-job-summary {
  grid-column: span 2;
}
.review-job-details > .input-main {
  width: 100%;
  min-width: 0;
  margin: 0;
}
.review-job-summary {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}
.review-summary-box {
  border: 1px solid gray;
  border-radius: 5px;
  padding: 2px 10px;
  white-space: nowrap;
}
.review-change-note {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 8px 12px;
  margin: 8px 0 2px;
  font-size: 11px;
}
.review-change-note > label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  white-space: nowrap;
}
.review-change-note input {
  margin: 0;
}
.review-job-description {
  margin-top: 5px;
}
.review-audit-warning {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #bdbdbd;
  font-size: 11px;
  line-height: 15px;
  color: #555;
  white-space: pre-line;
}
.review-sections {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.review-sections > *,
.review-job-box {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.review-table {
  width: 100%;
  max-width: 100%;
  border: 1px solid #bdbdbd;
  box-sizing: border-box;
  height: 100%;
  flex: 1;
  font-size: 12px;
}
.review-section-content,
.review-section-table {
  min-width: 0;
}
.review-section-table {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.review-tyre-layout {
  display: grid;
  grid-template-columns: minmax(280px, 2fr) minmax(0, 3fr);
  gap: 10px;
  align-items: stretch;
}
.review-tyre-grid {
  pointer-events: none;
  height: auto;
  padding: 0;
  justify-content: flex-start;
}
.review-tyre-grid ::v-deep .tyre-grid-legend {
  display: none;
}
.review-table ::v-deep .table-column[columnname='Change'] > .title-block {
  visibility: hidden;
}
.review-tyre-hint {
  margin: 6px 0 0;
  color: #596572;
  font-size: 11px;
}
.review-table ::v-deep .row-overlay {
  display: none;
}
.review-table ::v-deep .top-block {
  display: none;
}
.review-table ::v-deep .row-data {
  padding: 4px 5px;
  line-height: 16px;
}
.review-table ::v-deep .review-amount {
  display: block;
  width: 100%;
  text-align: right;
}
.review-table ::v-deep .review-wrap {
  white-space: pre-wrap;
}
.review-parts-total {
  padding: 4px 5px;
  text-align: right;
  border-top: 1px solid #bdbdbd;
}
.review-history .review-table ::v-deep .row-data {
  border-bottom: 1px solid #e6e6e6;
}
.review-empty {
  color: #596572;
  font-size: 11px;
  margin: 6px 0;
}
.review-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  min-height: 200px;
  color: black;
}
.review-decision-footer {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
  padding: 6px 8px 3px;
  box-sizing: border-box;
  border-top: 1px solid #ccc;
  background: white;
  text-align: left;
}
.review-decision-remark {
  flex: 1;
  min-width: 260px;
}
.review-decision-remark > * {
  width: 100%;
  margin: 0;
}
.review-decision-remark ::v-deep textarea {
  resize: none;
}
.review-footer-actions {
  display: flex;
  gap: 5px;
  margin-left: auto;
}
.review-footer-actions button {
  height: 35px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 6px;
  background: $btn-color;
}
.review-footer-actions button:hover:enabled {
  background: $btn-color-hover;
}
.review-footer-actions svg {
  height: 22px;
  width: 22px;
}
.review-decision-error {
  order: -1;
  color: #b3261e;
  width: 100%;
}
.invalid-remark ::v-deep textarea {
  border-color: #b3261e;
}
.decision-popup ::v-deep .content > .dragger {
  display: none;
}
.decision-popup ::v-deep .header,
.decision-popup ::v-deep .decision-footer {
  flex-shrink: 0;
}
.decision-popup ::v-deep .header {
  margin: 4px 0 8px;
}
.decision-popup ::v-deep .content > .decision-content {
  height: auto;
  min-height: 0;
  flex: 0 1 auto;
  overflow: auto;
  padding: 0 8px 4px;
}
.decision-popup ::v-deep .decision-footer {
  margin-top: 8px;
  border-top: 2px solid gray;
  padding-top: 6px;
}
.decision-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0 8px;
}
.decision-summary > label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  white-space: nowrap;
}
.decision-summary > label > input[type='checkbox'] {
  margin: 0;
  flex-shrink: 0;
}
.decision-document {
  padding: 12px 10px 6px;
  margin-top: 14px;
}
.decision-document ::v-deep > .title {
  display: none;
}
.decision-card-title {
  position: absolute;
  top: -10px;
  left: 10px;
  display: flex;
  align-items: center;
  max-width: calc(100% - 180px);
  padding: 0 5px;
  background: white;
}
.decision-card-title > :first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.decision-card-amount {
  position: absolute;
  top: -9px;
  right: 10px;
  padding: 0 5px;
  background: white;
  white-space: nowrap;
}
.decision-card-body {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}
.decision-details {
  min-width: 0;
  margin: 0;
}
.decision-details > div {
  display: grid;
  grid-template-columns: 104px minmax(0, 1fr);
  line-height: 17px;
}
.decision-details dt {
  font-weight: bold;
}
.decision-details dd {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.decision-details dd::before {
  content: ': ';
}
.decision-type {
  display: inline-block;
  flex-shrink: 0;
  padding: 1px 14px;
  margin-left: 8px;
}
.decision-type.doc-tag--workorder {
  background: rgb(255, 192, 0);
}
.decision-type.doc-tag--payment {
  background: rgb(0, 176, 240);
}
.decision-type.doc-tag--cheque {
  background: rgb(180, 150, 230);
}
.decision-document-remark,
.shared-decision-remark {
  width: 100%;
  height: auto;
  margin: 6px 0 0;
}
.decision-popup .invalid-remark ::v-deep textarea {
  border-color: #e53935;
  background: #fff0f0 !important;
}
.decision-review-state {
  display: flex;
  flex-wrap: wrap;
  column-gap: 6px;
  align-items: center;
}
.decision-review-state::before {
  margin-right: -3px;
}
.decision-review-state > span {
  color: #8a5a00;
  font-size: 11px;
}
.decision-review-state > .reviewed {
  color: #1f7a34;
}
.decision-attachments {
  min-width: 0;
}
.decision-attachment-header,
.decision-upload-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 22px;
}
.decision-upload {
  flex: 0 0 140px;
  min-width: 0;
  margin: 0;
}
.decision-upload ::v-deep .file-area {
  height: 25px;
}
.decision-upload ::v-deep .truncate-text {
  display: none;
}
.decision-file-names {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.decision-muted {
  color: #666;
}
.decision-attachment-remark {
  margin: 6px 0 0;
}
.decision-attachment-note {
  display: block;
  margin-top: 6px;
}
.review-documents-table ::v-deep button:disabled {
  background-color: #ddd;
  color: #555;
  opacity: 1;
}
.card-approval-date {
  font-size: 11px;
  line-height: 16px;
}
.api-message-history {
  min-height: 0;
  overflow: auto;
  padding: 8px;
  text-align: left;
}
.api-message-entry {
  padding: 8px 0;
  border-top: 1px solid #ccc;
  overflow-wrap: anywhere;
}
.api-message-success {
  color: #236536;
  background: #e6f4e9;
}
.api-message-error {
  color: #b3261e;
  background: #fde7e5;
}
.api-message-entry.api-message-success, .api-message-entry.api-message-error {
  margin-bottom: 5px;
  padding: 8px;
}
.api-message-summary {
  font-weight: bold;
  margin-bottom: 3px;
}
.api-message-text {
  white-space: pre-wrap;
}
.decision-footer {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}
.decision-messages {
  flex: 1;
  min-width: 0;
  min-height: 35px;
  padding: 5px 8px;
  background: #fff4bd;
  text-align: left;
}
.decision-required-message, .decision-required-documents {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 8px;
}
.decision-messages:empty {
  background: transparent;
}
.decision-buttons {
  display: flex;
  gap: 5px;
}
.decision-buttons > button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: 35px;
  min-width: 100px;
  margin: 0;
  padding: 4px 10px;
  background: #b3c4ff;
}
.decision-buttons img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}
button.approve-button {
  background: #9ee8b2;
  color: #222;
}
button.approve-button:hover:enabled {
  background: #afeec0;
}
button.reject-button {
  background: #f2a1a1;
  color: #222;
}
button.reject-button:hover:enabled {
  background: #f7b3b3;
}
.doc-page-main .amount-input-wrap {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  > .input-main {
    flex: 1;
    min-width: 0;
  }
  > .remember-amount-pin {
    flex: 0 0 24px;
    width: 24px;
    height: 20px;
    margin: 2px 0 0;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid lightgray !important;
    border-radius: 0.25rem;
    background-color: #fafafa;
    color: #405064;
    box-shadow: none;
    transition: none;
    cursor: pointer;
    &:hover:not(.active):not(:disabled) {
      background-color: #eef1ff;
    }
    &:focus-visible {
      outline: 2px solid #1f6fb2 !important;
      outline-offset: -2px;
    }
    &.active {
      background-color: #b3c4ff;
      border-color: #b3c4ff !important;
      color: #25345f;
    }
    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}
.doc-page-main .display-option {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  font-size: 11px;
  white-space: nowrap;
}
.doc-page-main .content-item.checked {
  background: #e2e4ff;
  border-color: #535cda !important;
}
.doc-page-main .resubmitted-flag {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 4px 0;
  padding: 3px 6px;
  border-radius: 3px;
  background: #fff4c2;
  color: #6b4f00;
  font-size: 11px;
  font-weight: 600;
  > img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
}
.doc-page-main .rejection-note {
  margin-top: 6px;
  background: rgba(255, 179, 179, 0.45);
  padding: 4px;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
.doc-page-main .card-date-amount {
  flex-wrap: wrap;
  gap: 6px;
  justify-content: space-between;
}
.doc-page-main .card-date-amount > * {
  white-space: nowrap;
}
.doc-page-main .card-date-amount > :first-child {
  width: auto !important;
  min-width: 0;
}
.doc-page-main .card-date-amount > .amount {
  margin-left: auto;
  text-align: right;
}
.doc-page-main .bucket .header .title {
  font-size: 12px;
  gap: 5px;
  white-space: normal;
}
.doc-page-main .bucket .header .title > label {
  display: flex;
  align-items: center;
  gap: 5px;
  justify-content: center;
}
@media (max-width: 1200px) {
  .review-job-details {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .doc-page-main > .main-column > .bottom-container {
    overflow-x: auto;
  }
  .doc-page-main > .main-column > .bottom-container > .bucket {
    min-width: 220px;
  }
}
@media (max-width: 760px) {
  .review-tyre-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .review-job-details {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .decision-card-body {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }
  .decision-card-title {
    max-width: calc(100% - 125px);
  }
  .decision-type {
    padding: 1px 6px;
    margin-left: 4px;
  }
  .decision-footer {
    flex-wrap: wrap;
  }
  .decision-messages {
    flex-basis: 100%;
  }
  .decision-buttons {
    margin-left: auto;
  }
  .review-change-note {
    flex-direction: column;
  }
  .doc-page-main > .main-column > .action-bar {
    flex-wrap: wrap;
  }
  .doc-page-main > .main-column > .action-bar > .filters-summary {
    flex-basis: 100%;
  }
}
</style>
